import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import "./updateItem.css";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import ImageUpload from "../../components/CustomComponents/ImageUpload/imageUpload";
import { ChoiceButtonGrid } from "../../components/CustomComponents/Buttons/ChoiceButton/choiceButton";
import CategorySelector from "../../components/CustomComponents/modal/CategorySelector";
import ModifierGroupsSelector from "../../components/CustomComponents/modal/ModifierGroupsSelector";
import AllergenSelector from "../../components/CustomComponents/modal/AllergenSelector";
import {
  resolveSelectedAllergens,
  getAllergenId,
  getAllergenKey,
  joinAllergenSolutions,
} from "../../components/CustomComponents/modal/allergenUtils.js";
import MenuGroupsSelector from "../../components/CustomComponents/modal/MenuGroupsSelector";
import ProductMultiSelectionModal from "../../components/CustomComponents/modal/productMultiSelectionModel";
import DebugMUIDatePicker from "../../components/CustomComponents/MUIDatePicker/DebugMUIDatePicker";
import "../../components/CustomComponents/MUIDatePicker/MUIDatePicker.css";
import DateTime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import "../../components/DateTimePicker/dateTimePicker.css";
import "../../components/DateTimePicker/materialUIOverrides.css";
import moment from "moment";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import ReportingCategoryModel from "../../components/CustomComponents/modal/reportingCategory";
import { Modifier } from "../modifiers/initializeModifier";
import "./UpdateItemShimmer.css";
import SupplierSelector from "../../components/CustomComponents/modal/SupplierSelector";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";

function flattenCategoryTreeForSync(cats, out = []) {
  if (!Array.isArray(cats)) return out;
  for (const c of cats) {
    if (c) out.push(c);
    if (c?.subCategoriesList?.length)
      flattenCategoryTreeForSync(c.subCategoriesList, out);
  }
  return out;
}

const getCategoryId = (catOrId) => {
  if (catOrId == null || catOrId === "") return null;
  if (typeof catOrId === "object") {
    const id = catOrId.rootCat_ID ?? catOrId.rC_ID ?? catOrId.reportCat_ID;
    return id == null || id === "" ? null : String(id);
  }
  return String(catOrId);
};

const categoryIdsMatch = (a, b) => {
  const idA = getCategoryId(a);
  const idB = getCategoryId(b);
  return idA != null && idB != null && idA === idB;
};

const findCategoryByReportId = (categories, reportCatId) => {
  if (!Array.isArray(categories)) return null;
  const target = getCategoryId(reportCatId);
  if (!target || target === "0") return null;
  return categories.find((cat) => cat && categoryIdsMatch(cat, target)) || null;
};

/**
 * Resolve reporting category for an item without blindly overwriting a saved reportCat_ID.
 * Falls back to first selected category only when no reportCat_ID is stored.
 */
const resolveReportingCategory = (
  categories,
  reportCatId,
  currentReportingCategory = null
) => {
  const list = Array.isArray(categories) ? categories.filter(Boolean) : [];
  const matched = findCategoryByReportId(list, reportCatId);
  if (matched) return matched;

  const savedId = getCategoryId(reportCatId);
  if (savedId && savedId !== "0") {
    if (
      currentReportingCategory &&
      categoryIdsMatch(currentReportingCategory, savedId)
    ) {
      return currentReportingCategory;
    }
    const numericId = Number(savedId);
    return {
      rootCat_ID: Number.isFinite(numericId) ? numericId : savedId,
      rC_ID: Number.isFinite(numericId) ? numericId : savedId,
      rootCat_Name:
        currentReportingCategory?.rootCat_Name ||
        currentReportingCategory?.rC_Name ||
        `Category #${savedId}`,
      rC_Name:
        currentReportingCategory?.rC_Name ||
        currentReportingCategory?.rootCat_Name ||
        `Category #${savedId}`,
    };
  }

  return list[0] || {};
};

/** Keep current reporting category when still selected; otherwise use first selected. */
const syncReportingCategoryWithSelection = (
  selectedCategories,
  currentReportingCategory
) => {
  const list = Array.isArray(selectedCategories)
    ? selectedCategories.filter(Boolean)
    : [];
  if (list.length === 0) return {};

  const currentId = getCategoryId(currentReportingCategory);
  if (currentId && currentId !== "0") {
    const stillSelected = list.find((cat) => categoryIdsMatch(cat, currentId));
    if (stillSelected) return stillSelected;
  }

  // No valid current selection — default to first selected category.
  return list[0] || {};
};

const UpdateItem = () => {
  const { ProductType, IsCombo, itemId } = useParams();
  const isComboItem = IsCombo === "Combo";
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData, settingsData } = useSelector((state) => state.auth);
  const { hasInitiallyLoaded } = useProfileAndSettings();
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  const [compCurrency, setCompCurrency] = useState("GBP");

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  const [formState, setFormState] = useState({
    menuItem_Name: "",
    menuItem_KitchenDisplayName: "",
    menuItem_Detail: "",
    menuItem_RootType: ProductType || "",
    menuItem_ColorCode: "",
    menuItem_SalePrice: "",
    menuItem_CostPrice: "",
    menuItem_TaxPercentage: "",
    menuItem_TakeAwayPrice: "",
    menuItem_DeliveryPrice: "",
    menuItem_ServingSize: "",
    menuItem_CaloriesPerServing: "",
    menuItem_PreparationTime: "",
    menuItem_SKU: "",
    menuItem_SEOTitle: "",
    menuItem_SEODescription: "",
    menuItem_Permalink: "",
    menuItem_ASTime: "",
    menuItem_AETime: "",
    menuItem_Rating: "",
    menuItem_BarCode: "",
    menuItem_Type: isComboItem ? "Combo" : "Product",
    menuItem_ImageAltText: "",
    retail_Markup: "",
    delivery_Markup: "",
    online_Markup: "",
    current_Quantity: "",
    reorder_Quantity: "",
    reorder_Level: "",
    availableQuantity: "",
    orderQuantityLimit: "",
    menuItem_DefaultModifierTitle: "",
    brand_ID: "",
    ingredientID: "",
    parentMenuItemID: "",
    itsMasterProductID: "",
    reportCat_ID: "",
    stadardUnit: "",
    unitSize: "",
    mP_UnitOfSale: "",
    mP_VolumnOfSale: "",
    localizeID: "en",
    appMode: "",
  });

  const [errors, setErrors] = useState({
    menuItem_Name: "",
    menuItem_SalePrice: "",
  });

  const [toggles, setToggles] = useState({
    menuItem_Publish: true,
    is_ShowInKiosk: true,
    isSellOnTill: true,
    isActiveMobileApp: true,
    is_OutOfStock: false,
    skuEnabled: false,
    expiryDateEnabled: true,
    seoEnabled: false,
    modifierGroupsEnabled: false,
    suppliersEnabled: false,
    allergensEnabled: false,
    menuGroupsEnabled: false,
    productsEnabled: isComboItem,
    menuItem_Homefeatured: false,
    menuItem_featured: false,
    menuItem_IsModifier: false,
    menuItem_IsFavorite: false,
    menuItem_HasVariant: false,
    isArchieve: false,
    isAllowedtoSetPriceatThePointofSale: false,
    isTaxExempt: false,
    isPreventGainingLoyalty: false,
    isHaveCustomerSpecificPricing: false,
    isProductisScannableOnly: false,
    isMasterProduct: false,
    itHasAMasterProduct: false,
    menuItem_IsDefaultModifier: false,
    isShowNotificationExpDate: false,
    isShowSocialMediaLink: false,
    IsSpecialItem: false,
  });

  const [imageState, setImageState] = useState({
    menuItem_Image: "",
    imageFile: null,
  });

  const [selectors, setSelectors] = useState({
    selectedParentCategory: [],
    selectedModifierGroups: [],
    selectedSuppliers: [],
    selectedAllergens: [],
    selectedMenuGroups: [],
    selectedProducts: [],
  });

  const [reportingCategory, setReportingCategory] = useState({});
  const [products, setProducts] = useState([]);
  const [allAllergens, setAllAllergens] = useState([]);
  const [settings, setSettings] = useState({ s_VAT: "", isTaxEnabled: false });
  const [hasUserEditedTax, setHasUserEditedTax] = useState(false);
  const [modals, setModals] = useState({
    showParentCategoryModal: false,
    showReportingCategoryModal: false,
    showModifierModal: false,
    showSupplierModal: false,
    showAllergenModal: false,
    showMenuGroupModal: false,
    isModalOpen: false,
    showProductModal: false,
  });
  const [expiryDate, setExpiryDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  /** Support lists (allergens/products/settings) finished - needed before dirty baseline. */
  const [catalogReady, setCatalogReady] = useState(false);
  /** True after post-load sync effects have settled and baseline snapshot is locked. */
  const [baselineReady, setBaselineReady] = useState(false);
  const [initialLoadedDataSnapshot, setInitialLoadedDataSnapshot] = useState(null);
  const [variants, setVariants] = useState([{ ...Modifier, reportCat_ID: "", menuItem_ServingSize: "", menuItem_TaxPercentage: "" }]);
  /** True when this row is a variant child (not the parent configurable product). */
  const [isVariantLineItem, setIsVariantLineItem] = useState(false);
  const [variantDetailModalOpen, setVariantDetailModalOpen] = useState(false);
  const [variantDetailIndex, setVariantDetailIndex] = useState(null);
  const [variantDetailDraft, setVariantDetailDraft] = useState(null);
  const [variantDetailExpiry, setVariantDetailExpiry] = useState(null);
  const isDarkMode = useDarkMode();
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const hasFetchedDetailsRef = useRef(false);
  const getFormSnapshotRef = useRef(() => "");

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const dedupeModifierGroups = (groups) => {
    if (!Array.isArray(groups)) return [];
    const seen = new Set();
    return groups.filter((g) => {
      const id = g?.modifierGroupID ?? g?.ModifierGroupID;
      if (id == null || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  const fetchCompCurrency = useCallback(async () => {
    try {
      if (!hasInitiallyLoaded) return;
      if (!profileData) {
        showErrorToast("Profile data not available. Please refresh the page.");
        return;
      }
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      showErrorToast("Failed to load company currency");
    }
  }, [profileData, hasInitiallyLoaded]);

  const fetchSettings = useCallback(async () => {
    try {
      if (!hasInitiallyLoaded) return;
      // Use Redux state instead of API call
      if (!settingsData) {
        setSettings({ s_VAT: 0, isTaxEnabled: false });
        return;
      }
      const newSettings = {
        s_VAT: settingsData.s_VAT || 0,
        isTaxEnabled: settingsData.isTaxEnabled ?? false,
      };
      setSettings(newSettings);
    } catch (err) {
      showErrorToast("Failed to load settings");
    }
  }, [settingsData, hasInitiallyLoaded]);

  const fetchAllergens = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        "/api/Allergen/GetAllAllergensGlobal"
      );
      setAllAllergens(response.data.data || []);
    } catch (err) {
      console.error("Error fetching allergens:", err);
      setAllAllergens([]);
      showErrorToast("Failed to fetch allergens");
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    if (!CompID) return;
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_ONLY_PRODUCTS, {
        params: { CompID },
      });
      setProducts(response.data.data || response.data);
    } catch (err) {
      setProducts([]);
      showErrorToast("Failed to fetch products");
    }
  }, [CompID]);

  const fetchItemDetails = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ITEM_BY_ID, {
        params: { CompID, MenuItem_ID: itemId },
      });

      const itemData = response.data.data || response.data;
      const isCombo = itemData.menuItem_Type === "Combo" || isComboItem;

      const variantParentId = Number(
        itemData.menuitemVariantParentID ??
          itemData.menuitem_VariantParentID ??
          itemData.menuItem_VariantParentID ??
          itemData.Menuitem_VariantParentID ??
          0
      );
      setIsVariantLineItem(
        variantParentId > 0 ||
          itemData.menuItem_IsVariant === true ||
          String(itemData.menuItem_Type || "").toLowerCase() === "variant"
      );

      setFormState((prev) => ({
        ...prev,
        menuItem_Name: itemData.menuItem_Name || "",
        menuItem_KitchenDisplayName: itemData.menuItem_KitchenDisplayName || "",
        menuItem_Detail: itemData.menuItem_Detail || "",
        menuItem_RootType: itemData.menuItem_RootType || ProductType || "",
        menuItem_ColorCode: itemData.menuItem_ColorCode || "",
        menuItem_SalePrice: (itemData.menuItem_SalePrice && parseFloat(itemData.menuItem_SalePrice) !== 0) ? itemData.menuItem_SalePrice.toString() : "",
        menuItem_CostPrice: (itemData.menuItem_CostPrice && parseFloat(itemData.menuItem_CostPrice) !== 0) ? itemData.menuItem_CostPrice.toString() : "",
        menuItem_TaxPercentage: itemData.isTaxExempt
          ? ""
          : (itemData.menuItem_TaxPercentage != null && itemData.menuItem_TaxPercentage !== "")
            ? String(itemData.menuItem_TaxPercentage)
            : (itemData.menuItem_TaxPercentage === 0 || itemData.menuItem_TaxPercentage === "0" ? "0" : ""),
        menuItem_TakeAwayPrice: (itemData.menuItem_TakeAwayPrice && parseFloat(itemData.menuItem_TakeAwayPrice) !== 0) ? itemData.menuItem_TakeAwayPrice.toString() : "",
        menuItem_DeliveryPrice: (itemData.menuItem_DeliveryPrice && parseFloat(itemData.menuItem_DeliveryPrice) !== 0) ? itemData.menuItem_DeliveryPrice.toString() : "",
        menuItem_ServingSize: itemData.menuItem_ServingSize || "",
        menuItem_CaloriesPerServing:
          itemData.menuItem_CaloriesPerServing != null &&
          itemData.menuItem_CaloriesPerServing !== ""
            ? String(itemData.menuItem_CaloriesPerServing)
            : "",
        menuItem_PreparationTime: itemData.menuItem_PreparationTime || "",
        menuItem_SKU: itemData.menuItem_SKU || "",
        menuItem_SEOTitle: itemData.menuItem_SEOTitle || "",
        menuItem_SEODescription: itemData.menuItem_SEODescription || "",
        menuItem_Permalink: itemData.menuItem_Permalink || "",
        menuItem_ASTime: itemData.menuItem_ASTime || "",
        menuItem_AETime: itemData.menuItem_AETime || "",
        menuItem_Rating: itemData.menuItem_Rating || "",
        menuItem_BarCode: itemData.menuItem_BarCode || "",
        menuItem_Type: isCombo ? "Combo" : "Product",
        menuItem_ImageAltText: itemData.menuItem_ImageAltText || "",
        retail_Markup: itemData.retail_Markup || "",
        delivery_Markup: itemData.delivery_Markup || "",
        online_Markup: itemData.online_Markup || "",
        current_Quantity: itemData.current_Quantity || "",
        reorder_Quantity: itemData.reorder_Quantity || "",
        reorder_Level: itemData.reorder_Level || "",
        availableQuantity: itemData.availableQuantity || "",
        orderQuantityLimit: itemData.orderQuantityLimit || "",
        menuItem_DefaultModifierTitle: itemData.menuItem_DefaultModifierTitle || "",
        brand_ID: itemData.brand_ID || "",
        ingredientID: itemData.ingredientID || "",
        parentMenuItemID: itemData.parentMenuItemID || "",
        itsMasterProductID: itemData.itsMasterProductID || "",
        reportCat_ID: itemData.reportCat_ID || "",
        stadardUnit: itemData.stadardUnit || "",
        unitSize: itemData.unitSize || "",
        mP_UnitOfSale: itemData.mP_UnitOfSale || "",
        mP_VolumnOfSale: itemData.mP_VolumnOfSale || "",
        localizeID: itemData.localizeID || "en",
        appMode: itemData.appMode || "",
      }));

      setToggles({
        menuItem_Publish: itemData.menuItem_Publish || false,
        is_ShowInKiosk: itemData.is_ShowInKiosk || false,
        isSellOnTill: itemData.isSellOnTill || false,
        isActiveMobileApp: itemData.isActiveMobileApp || false,
        is_OutOfStock: itemData.is_OutOfStock || false,
        skuEnabled: !!itemData.menuItem_SKU,
        expiryDateEnabled:
          itemData.menuItem_ExpiryDate &&
          itemData.menuItem_ExpiryDate !== "0001-01-01T00:00:00",
        seoEnabled: !!(
          itemData.menuItem_SEOTitle ||
          itemData.menuItem_SEODescription ||
          itemData.menuItem_Permalink
        ),
        modifierGroupsEnabled: itemData.modifierGroups?.length > 0 || false,
        suppliersEnabled: itemData.suppliers?.length > 0 || false,
        allergensEnabled: itemData.hasAllergen || false,
        menuGroupsEnabled: itemData.menuGroups?.length > 0 || false,
        productsEnabled: isCombo && (itemData.products?.length > 0 || isComboItem),
        menuItem_Homefeatured: itemData.menuItem_Homefeatured || false,
        menuItem_featured: itemData.menuItem_featured || false,
        menuItem_IsModifier: itemData.menuItem_IsModifier || false,
        menuItem_IsFavorite: itemData.menuItem_IsFavorite || false,
        menuItem_HasVariant: itemData.menuItem_HasVariant || false,
        isArchieve: itemData.isArchieve || false,
        isAllowedtoSetPriceatThePointofSale:
          itemData.isAllowedtoSetPriceatThePointofSale || false,
        isTaxExempt: itemData.isTaxExempt || false,
        isPreventGainingLoyalty: itemData.isPreventGainingLoyalty || false,
        isHaveCustomerSpecificPricing: itemData.isHaveCustomerSpecificPricing || false,
        isProductisScannableOnly: itemData.isProductisScannableOnly || false,
        isMasterProduct: itemData.isMasterProduct || false,
        itHasAMasterProduct: itemData.itHasAMasterProduct || false,
        menuItem_IsDefaultModifier: itemData.menuItem_IsDefaultModifier || false,
        isShowNotificationExpDate: itemData.isShowNotificationExpDate || false,
        isShowSocialMediaLink: itemData.isShowSocialMediaLink || false,
        IsSpecialItem: itemData.IsSpecialItem || itemData.isSpecialItem || false,
      });

      setImageState({
        menuItem_Image: itemData.menuItem_Image || "",
        imageFile: null,
      });

      setSelectors((prev) => ({
        ...prev,
        selectedParentCategory: itemData.categories || [],
        selectedModifierGroups: dedupeModifierGroups(itemData.modifierGroups || []),
        selectedSuppliers: itemData.suppliers || [],
        selectedAllergens: resolveSelectedAllergens(
          itemData.allergens,
          allAllergens
        ),
        selectedMenuGroups: itemData.menuGroups || [],
        selectedProducts:
          itemData.products?.map((product) => ({
            ...product,
            availableQuantity: product.availableQuantity || "1",
          })) || [],
      }));

      setReportingCategory(
        resolveReportingCategory(
          itemData.categories || [],
          itemData.reportCat_ID
        )
      );

      if (itemData.menuItem_HasVariant) {
        const existingVariants = Array.isArray(itemData.products)
          ? itemData.products.map((variant) => ({
              ...variant,
              reportCat_ID: itemData.reportCat_ID || "",
              menuItem_ServingSize: variant.menuItem_ServingSize || "",
              menuItem_CaloriesPerServing:
                variant.menuItem_CaloriesPerServing != null &&
                variant.menuItem_CaloriesPerServing !== ""
                  ? String(variant.menuItem_CaloriesPerServing)
                  : "",
              __taxEdited: false,
              menuItem_TaxPercentage: (variant.menuItem_TaxPercentage != null && variant.menuItem_TaxPercentage !== "") || variant.menuItem_TaxPercentage === 0 || variant.menuItem_TaxPercentage === "0"
                ? String(variant.menuItem_TaxPercentage)
                : "",
            }))
          : [];
        const defaultTax = !itemData.isTaxExempt && settingsData?.isTaxEnabled && settingsData?.s_VAT
          ? settingsData.s_VAT.toString()
          : "";
        setVariants([...existingVariants, { 
          ...Modifier, 
          reportCat_ID: itemData.reportCat_ID || "", 
          menuItem_ServingSize: "",
          __taxEdited: false,
          menuItem_TaxPercentage: defaultTax,
        }]);
      } else {
        const defaultTax = !itemData.isTaxExempt && settingsData?.isTaxEnabled && settingsData?.s_VAT
          ? settingsData.s_VAT.toString()
          : "";
        setVariants([{ 
          ...Modifier, 
          reportCat_ID: itemData.reportCat_ID || "", 
          menuItem_ServingSize: "",
          __taxEdited: false,
          menuItem_TaxPercentage: defaultTax,
        }]);
      }

      setExpiryDate(
        itemData.menuItem_ExpiryDate &&
          itemData.menuItem_ExpiryDate !== "0001-01-01T00:00:00"
          ? new Date(itemData.menuItem_ExpiryDate)
          : null
      );

      setHasUserEditedTax(false);
    } catch (error) {
      showErrorToast("Failed to load item details");
    } finally {
      setInitialLoading(false);
    }
  }, [CompID, itemId, ProductType, isComboItem, settingsData, allAllergens]);

  useEffect(() => {
    let cancelled = false;
    setCatalogReady(false);
    const handler = setTimeout(async () => {
      try {
        await Promise.all([
          fetchSettings(),
          fetchCompCurrency(),
          fetchAllergens(),
          fetchProducts(),
        ]);
      } finally {
        if (!cancelled) setCatalogReady(true);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(handler);
    };
  }, [fetchSettings, fetchCompCurrency, fetchAllergens, fetchProducts]);

  useEffect(() => {
    if (!hasFetchedDetailsRef.current && settingsData?.s_VAT !== undefined) {
      hasFetchedDetailsRef.current = true;
      fetchItemDetails();
    }
  }, [settingsData?.s_VAT, fetchItemDetails]);

  // Fallback: fetch item details after a timeout if it hasn't been fetched yet
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!hasFetchedDetailsRef.current) {
        hasFetchedDetailsRef.current = true;
        fetchItemDetails();
      }
    }, 3000); // 3 second fallback

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!allAllergens.length) return;
    setSelectors((prev) => {
      const next = resolveSelectedAllergens(prev.selectedAllergens, allAllergens);
      const prevKeys = (prev.selectedAllergens || [])
        .map((a, i) => `${getAllergenKey(a, i)}:${getAllergenId(a) ?? ""}`)
        .join("|");
      const nextKeys = next
        .map((a, i) => `${getAllergenKey(a, i)}:${getAllergenId(a) ?? ""}`)
        .join("|");
      if (prevKeys === nextKeys) return prev;
      return { ...prev, selectedAllergens: next };
    });
  }, [allAllergens]);

  useEffect(() => {
    if (reportingCategory?.rootCat_ID || reportingCategory?.rC_ID) {
      const reportCatId = reportingCategory.rootCat_ID || reportingCategory.rC_ID;
      setVariants((prevVariants) => {
        if (prevVariants.every((v) => String(v.reportCat_ID ?? "") === String(reportCatId))) {
          return prevVariants;
        }
        return prevVariants.map((variant) => ({
          ...variant,
          reportCat_ID: reportCatId,
        }));
      });
    }
  }, [reportingCategory?.rootCat_ID, reportingCategory?.rC_ID]);

  // Variants: show default s_VAT when empty on load, but never overwrite after user edits/clears or sets 0.
  useEffect(() => {
    if (toggles.isTaxExempt) {
      setVariants((prev) => {
        if (prev.every((v) => v.menuItem_TaxPercentage === "" && !v.__taxEdited)) {
          return prev;
        }
        return prev.map((v) => ({ ...v, menuItem_TaxPercentage: "", __taxEdited: false }));
      });
      return;
    }
    if (!settingsData?.isTaxEnabled || !settingsData?.s_VAT) return;
    const defaultTax = settingsData.s_VAT.toString();
    setVariants((prev) => {
      let changed = false;
      const next = prev.map((v) => {
        const taxEmpty = v.menuItem_TaxPercentage == null || v.menuItem_TaxPercentage === "";
        if (!v.__taxEdited && taxEmpty) {
          changed = true;
          return { ...v, menuItem_TaxPercentage: defaultTax };
        }
        return v;
      });
      return changed ? next : prev;
    });
  }, [toggles.isTaxExempt, settingsData?.isTaxEnabled, settingsData?.s_VAT]);

  useEffect(() => {
    setReportingCategory((prev) => {
      const next = syncReportingCategoryWithSelection(
        selectors.selectedParentCategory,
        prev
      );
      const prevId = prev?.rootCat_ID ?? prev?.rC_ID ?? null;
      const nextId = next?.rootCat_ID ?? next?.rC_ID ?? null;
      if (String(prevId ?? "") === String(nextId ?? "")) return prev;
      return next;
    });
  }, [selectors.selectedParentCategory]);

  // Sync main serving size with default variant's serving size on variants change
  useEffect(() => {
    if (toggles.menuItem_HasVariant) {
      const defaultVariant = variants.find(v => v.menuItem_IsDefaultVariant) || variants[0];
      const nextServing = defaultVariant?.menuItem_ServingSize || "";
      const nextCalories =
        defaultVariant?.menuItem_CaloriesPerServing != null &&
        defaultVariant?.menuItem_CaloriesPerServing !== ""
          ? String(defaultVariant.menuItem_CaloriesPerServing)
          : "";
      setFormState((prev) => {
        if (
          (prev.menuItem_ServingSize || "") === nextServing &&
          (prev.menuItem_CaloriesPerServing || "") === nextCalories
        ) {
          return prev;
        }
        return {
          ...prev,
          menuItem_ServingSize: nextServing,
          menuItem_CaloriesPerServing: nextCalories,
        };
      });
    }
  }, [variants, toggles.menuItem_HasVariant]);

  

  // Set CSS custom property for theme color
  useEffect(() => {
    document.documentElement.style.setProperty('--theme-color', themeColor);
  }, [appTheme]);

  // Main tax: show default s_VAT when empty on load, but never overwrite after user edits/clears or sets 0.
  useEffect(() => {
    if (toggles.isTaxExempt) {
      setHasUserEditedTax(false);
      setFormState((prev) =>
        prev.menuItem_TaxPercentage === ""
          ? prev
          : { ...prev, menuItem_TaxPercentage: "" }
      );
      setVariants((prev) => {
        const list = prev || [];
        if (list.every((v) => v.menuItem_TaxPercentage === "")) return list;
        return list.map((variant) => ({
          ...variant,
          menuItem_TaxPercentage: "",
        }));
      });
      return;
    }
    if (
      !hasUserEditedTax &&
      settingsData?.isTaxEnabled &&
      settingsData?.s_VAT &&
      (formState.menuItem_TaxPercentage == null || formState.menuItem_TaxPercentage === "")
    ) {
      setFormState((prev) => ({
        ...prev,
        menuItem_TaxPercentage: settingsData.s_VAT.toString(),
      }));
    }
  }, [
    toggles.isTaxExempt,
    settingsData?.isTaxEnabled,
    settingsData?.s_VAT,
    hasUserEditedTax,
    formState.menuItem_TaxPercentage,
  ]);


  const handleInputChange = (field) => (e) => {
    let value = e.target.value;
    
    // Handle price fields: if current value is "0" and user starts typing, clear it
    if (["menuItem_SalePrice", "menuItem_TakeAwayPrice", "menuItem_DeliveryPrice", "menuItem_CostPrice"].includes(field)) {
      const currentValue = formState[field];
      // If current value is "0" and user is typing a digit, replace "0" with the new value
      if (currentValue === "0" && value.length > 0 && /^\d/.test(value)) {
        value = value.replace(/^0+/, "") || "";
      }
    }
    
    if (field === "menuItem_TaxPercentage") {
      if (toggles.isTaxExempt) {
        setFormState((prev) => ({ ...prev, menuItem_TaxPercentage: "" }));
        return;
      }
      if (value === "" || /^\d*\.?\d*$/.test(value)) {
        setHasUserEditedTax(true);
        setFormState((prev) => ({ ...prev, menuItem_TaxPercentage: value }));
      } else {
        showErrorToast("Tax Percentage must be a valid number (e.g., 20)");
      }
      return;
    }
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (field === "menuItem_Name") {
      setErrors((prev) => ({
        ...prev,
        menuItem_Name: value.trim() ? "" : "Item Name is required",
      }));
    }
    if (field === "menuItem_PreparationTime" && value && !/^\d+$/.test(value)) {
      showErrorToast("Preparation Time must be a positive number (e.g., 40)");
      return;
    }
    if (field === "menuItem_CaloriesPerServing" && value && !/^\d+$/.test(value)) {
      showErrorToast("Calories per serving must be a whole number (e.g., 450)");
      return;
    }
    if (field === "menuItem_SalePrice" && value) {
      if (!/^\d*\.?\d*$/.test(value)) {
        showErrorToast("Sale Price must be a valid number (e.g., 100)");
        return;
      }
    }
    if (field === "menuItem_CostPrice" && value && !/^\d*\.?\d*$/.test(value)) {
      showErrorToast("Cost Price must be a valid number (e.g., 10)");
      return;
    }
  };

  const handleProductQuantityChange = (productId, value) => {
    if (value && !/^\d+$/.test(value)) {
      showErrorToast("Item Quantity must be a positive number");
      return;
    }
    if (parseInt(value) <= 0) {
      showErrorToast("Item Quantity must be greater than 0");
      return;
    }
    setSelectors((prev) => ({
      ...prev,
      selectedProducts: prev.selectedProducts?.map((product) =>
        product.menuItem_ID === productId
          ? { ...product, availableQuantity: value }
          : product
      ),
    }));
  };

  const handleToggle = (field) => () => {
    setToggles((prev) => {
      const newToggles = { ...prev, [field]: !prev[field] };
      if (field === "productsEnabled" && !newToggles.productsEnabled) {
        setSelectors((prev) => ({ ...prev, selectedProducts: [] }));
      } else if (field === "isTaxExempt") {
        setFormState((prev) => ({
          ...prev,
          menuItem_TaxPercentage: newToggles.isTaxExempt ? "" : (prev.menuItem_TaxPercentage ?? ""),
        }));
      }
      if (
        field === "productsEnabled" &&
        newToggles.productsEnabled &&
        formState.menuItem_Type === "Combo"
      ) {
        fetchProducts();
      }
      return newToggles;
    });
  };

  const handleVariantChange = (index, field, value) => {
    if (field === "menuItem_CaloriesPerServing" && value && !/^\d+$/.test(value)) {
      showErrorToast("Calories per serving must be a whole number (e.g., 450)");
      return;
    }
    
    // Handle price fields: if current value is "0" and user starts typing, clear it
    if (["menuItem_SalePrice", "menuItem_TakeAwayPrice", "menuItem_DeliveryPrice", "menuItem_CostPrice"].includes(field)) {
      const currentValue = variants[index]?.[field];
      // If current value is "0" or 0 and user is typing a digit, replace "0" with the new value
      if ((currentValue === "0" || currentValue === 0) && value.length > 0 && /^\d/.test(value)) {
        value = value.replace(/^0+/, "") || "";
      }
    }
    
    let updatedVariants = variants.map((variant, i) =>
      i === index
        ? {
            ...variant,
            [field]: value,
            reportCat_ID: (reportingCategory.rootCat_ID || reportingCategory.rC_ID) || "",
            ...(field === "menuItem_TaxPercentage" ? { __taxEdited: true } : {}),
          }
        : variant
    );
    if (field === "menuItem_IsDefaultVariant" && value === true) {
      updatedVariants = updatedVariants.map((variant, i) => ({
        ...variant,
        menuItem_IsDefaultVariant: i === index ? true : false,
      }));

      const defaultVariant = updatedVariants[index];

      setFormState((prev) => ({
        ...prev,
        menuItem_SalePrice: defaultVariant.menuItem_SalePrice || "0.00",
        menuItem_DeliveryPrice: defaultVariant.menuItem_DeliveryPrice || "0.00",
        menuItem_TakeAwayPrice: defaultVariant.menuItem_TakeAwayPrice || "0.00",
        menuItem_TaxPercentage: defaultVariant.menuItem_TaxPercentage || "0.00",
        menuItem_CostPrice: defaultVariant.menuItem_CostPrice || "0.00",
        menuItem_ServingSize: defaultVariant.menuItem_ServingSize || "",
        menuItem_CaloriesPerServing:
          defaultVariant.menuItem_CaloriesPerServing != null &&
          defaultVariant.menuItem_CaloriesPerServing !== ""
            ? String(defaultVariant.menuItem_CaloriesPerServing)
            : "",
      }));
    }

    setVariants(updatedVariants);

    // Sync main serving size if this variant is default and field is serving size
    if (field === "menuItem_ServingSize" && updatedVariants[index].menuItem_IsDefaultVariant) {
      setFormState((prev) => ({
        ...prev,
        menuItem_ServingSize: value,
      }));
    }

    if (field === "menuItem_CaloriesPerServing" && updatedVariants[index].menuItem_IsDefaultVariant) {
      setFormState((prev) => ({
        ...prev,
        menuItem_CaloriesPerServing: value,
      }));
    }

    if (field === "menuItem_Name" && value && index === variants.length - 1) {
      const defaultTax = !toggles.isTaxExempt && settingsData?.isTaxEnabled && settingsData?.s_VAT
        ? settingsData.s_VAT.toString()
        : "";
      setVariants([...updatedVariants, { 
        ...Modifier, 
        reportCat_ID: (reportingCategory.rootCat_ID || reportingCategory.rC_ID) || "", 
        menuItem_ServingSize: "",
        __taxEdited: false,
        menuItem_TaxPercentage: defaultTax,
      }]);
    }
  };

  const parseVariantExpiryToDate = (v) => {
    const raw = v?.menuItem_ExpiryDate;
    if (!raw || raw === "0001-01-01T00:00:00") return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const normalizeVariantForDetailDraft = (v) => ({
    ...v,
    menuItem_Name: v.menuItem_Name ?? "",
    menuItem_KitchenDisplayName: v.menuItem_KitchenDisplayName ?? "",
    menuItem_ServingSize: v.menuItem_ServingSize ?? "",
    menuItem_CaloriesPerServing:
      v.menuItem_CaloriesPerServing != null && v.menuItem_CaloriesPerServing !== ""
        ? String(v.menuItem_CaloriesPerServing)
        : "",
    menuItem_SKU: v.menuItem_SKU ?? "",
    menuItem_BarCode: v.menuItem_BarCode ?? "",
    current_Quantity:
      v.current_Quantity != null && v.current_Quantity !== ""
        ? String(v.current_Quantity)
        : "",
    reorder_Quantity:
      v.reorder_Quantity != null && v.reorder_Quantity !== ""
        ? String(v.reorder_Quantity)
        : "",
    reorder_Level:
      v.reorder_Level != null && v.reorder_Level !== ""
        ? String(v.reorder_Level)
        : "",
    availableQuantity:
      v.availableQuantity != null && v.availableQuantity !== ""
        ? String(v.availableQuantity)
        : "",
  });

  const openVariantDetailModal = (index) => {
    const v = variants[index];
    if (v == null) return;
    const { __taxEdited, ...rest } = v;
    setVariantDetailDraft(normalizeVariantForDetailDraft(rest));
    setVariantDetailExpiry(parseVariantExpiryToDate(v));
    setVariantDetailIndex(index);
    setVariantDetailModalOpen(true);
  };

  const closeVariantDetailModal = () => {
    setVariantDetailModalOpen(false);
    setVariantDetailIndex(null);
    setVariantDetailDraft(null);
    setVariantDetailExpiry(null);
  };

  const updateVariantDetailDraft = (field, value) => {
    setVariantDetailDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const setVariantDetailPublish = (checked) => {
    setVariantDetailDraft((prev) => {
      if (!prev) return prev;
      if (checked) {
        return { ...prev, menuItem_Publish: true };
      }
      return { ...prev, menuItem_Publish: false };
    });
  };

  const saveVariantDetailModal = () => {
    if (variantDetailIndex == null || variantDetailDraft == null) return;

    if (
      variantDetailDraft.menuItem_CaloriesPerServing &&
      !/^\d+$/.test(String(variantDetailDraft.menuItem_CaloriesPerServing))
    ) {
      showErrorToast("Calories per serving must be a whole number (e.g., 450)");
      return;
    }

    const expiryIso = variantDetailExpiry ? variantDetailExpiry.toISOString() : null;

    setVariants((prev) => {
      const orig = prev[variantDetailIndex];
      const taxChanged =
        String(variantDetailDraft.menuItem_TaxPercentage ?? "") !==
        String(orig?.menuItem_TaxPercentage ?? "");
      let next = prev.map((row, i) => {
        if (i !== variantDetailIndex) return row;
        return {
          ...row,
          ...variantDetailDraft,
          menuItem_ExpiryDate: expiryIso,
          __taxEdited: row.__taxEdited || taxChanged,
        };
      });

      if (variantDetailDraft.menuItem_IsDefaultVariant) {
        next = next.map((row, i) => ({
          ...row,
          menuItem_IsDefaultVariant: i === variantDetailIndex,
        }));
        const defaultVariant = next[variantDetailIndex];
        setFormState((p) => ({
          ...p,
          menuItem_SalePrice: defaultVariant.menuItem_SalePrice || "0.00",
          menuItem_DeliveryPrice: defaultVariant.menuItem_DeliveryPrice || "0.00",
          menuItem_TakeAwayPrice: defaultVariant.menuItem_TakeAwayPrice || "0.00",
          menuItem_TaxPercentage: defaultVariant.menuItem_TaxPercentage || "0.00",
          menuItem_CostPrice: defaultVariant.menuItem_CostPrice || "0.00",
          menuItem_ServingSize: defaultVariant.menuItem_ServingSize || "",
          menuItem_CaloriesPerServing:
            defaultVariant.menuItem_CaloriesPerServing != null &&
            defaultVariant.menuItem_CaloriesPerServing !== ""
              ? String(defaultVariant.menuItem_CaloriesPerServing)
              : "",
        }));
      }

      return next;
    });

    closeVariantDetailModal();
  };

  const handleVariantRowClick = (e, index) => {
    if (
      e.target.closest(
        "input, textarea, select, button, .toggle-switch, .switch, .slider, label.switch, .fa-trash, .fa-bars, .fa-eye"
      )
    ) {
      return;
    }
    openVariantDetailModal(index);
  };

  const handleRemoveVariant = (index) => {
    const remaining = variants.filter((_, i) => i !== index);
    const defaultTax = !toggles.isTaxExempt && settingsData?.isTaxEnabled && settingsData?.s_VAT
      ? settingsData.s_VAT.toString()
      : "";
    if (remaining.length === 0) {
      setVariants([{ 
        ...Modifier, 
        reportCat_ID: (reportingCategory.rootCat_ID || reportingCategory.rC_ID) || "", 
        menuItem_ServingSize: "",
        __taxEdited: false,
        menuItem_TaxPercentage: defaultTax,
      }]);
      return;
    }
    const last = remaining[remaining.length - 1];
    if (last && (last.menuItem_Name || "").trim()) {
      setVariants([...remaining, { 
        ...Modifier, 
        reportCat_ID: (reportingCategory.rootCat_ID || reportingCategory.rC_ID) || "", 
        menuItem_ServingSize: "",
        __taxEdited: false,
        menuItem_TaxPercentage: defaultTax,
      }]);
    } else {
      setVariants(remaining);
    }
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;
    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }
    const newVariants = [...variants];
    const draggedVariant = newVariants[draggedIndex];
    newVariants.splice(draggedIndex, 1);
    newVariants.splice(overIndex, 0, draggedVariant);
    setVariants(newVariants);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const disablePastDates = (current) => {
    return !current.isSameOrAfter(moment().startOf("day"));
  };

  const handleASTimeChange = (time) => {
    const formattedTime = moment(time).format("HH:mm");
    setFormState((prev) => ({ ...prev, menuItem_ASTime: formattedTime }));
  };

  const handleAETimeChange = (time) => {
    const formattedTime = moment(time).format("HH:mm");
    setFormState((prev) => ({ ...prev, menuItem_AETime: formattedTime }));
  };

  const handleProductTypeSelect = (type) => {
    setFormState((prev) => ({ ...prev, menuItem_RootType: type }));
    setModals((prev) => ({ ...prev, isModalOpen: false }));
  };

  const handleReportingCategorySubmit = (categoryData) => {
    setReportingCategory(categoryData || {});
    setModals((prev) => ({ ...prev, showReportingCategoryModal: false }));
  };

  const refreshReportingCategoriesFromTree = useCallback(async () => {
    if (!CompID) return;
    try {
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: {
          CompID,
          SortBy: "RootCat_Name",
          SortOrder: "ASC",
          Search: "",
        },
      });
      const tree = response.data.data || response.data;
      if (!Array.isArray(tree)) return;
      const flat = flattenCategoryTreeForSync(tree);
      setSelectors((prev) => ({
        ...prev,
        selectedParentCategory: prev.selectedParentCategory.map((sel) => {
          const id = sel?.rootCat_ID ?? sel?.rC_ID;
          if (id == null) return sel;
          const match = flat.find(
            (c) =>
              String(c?.rootCat_ID) === String(id) ||
              String(c?.rC_ID) === String(id)
          );
          return match ? { ...sel, ...match } : sel;
        }),
      }));
    } catch (err) {
      showErrorToast("Could not refresh categories.");
    }
  }, [CompID]);

  const removeProduct = (productId) => {
    setSelectors((prev) => ({
      ...prev,
      selectedProducts: prev.selectedProducts.filter(
        (p) => p.menuItem_ID !== productId
      ),
    }));
  };

  const handleSelectorSubmit = (key) => (selectedItems) => {
    if (key === "selectedProducts") {
      const updatedItems = selectedItems?.map((item) => ({
        ...item,
        availableQuantity: item.availableQuantity || "1",
      }));
      setSelectors((prev) => ({ ...prev, [key]: updatedItems }));
    } else {
      if (key === "selectedAllergens") {
        setSelectors((prev) => ({
          ...prev,
          selectedAllergens: resolveSelectedAllergens(selectedItems, allAllergens),
        }));
      } else {
        setSelectors((prev) => ({
          ...prev,
          [key]: Array.isArray(selectedItems) ? selectedItems : [],
        }));
      }

      if (key === "selectedParentCategory") {
        const categories = Array.isArray(selectedItems) ? selectedItems : [];
        setReportingCategory((prev) =>
          syncReportingCategoryWithSelection(categories, prev)
        );
      }
    }

    setModals((prev) => ({
      ...prev,
      [`show${key.charAt(0).toUpperCase() + key.slice(1)}Modal`]: false,
    }));
  };

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      formState,
      toggles,
      menuItem_Image: imageState.menuItem_Image || "",
      imageFileName: imageState.imageFile?.name || "",
      selectors: {
        selectedParentCategory: (selectors.selectedParentCategory || []).map(
          (c) => c.rootCat_ID ?? c.rC_ID
        ),
        selectedModifierGroups: (selectors.selectedModifierGroups || []).map(
          (g) => g.modifierGroupID ?? g.ModifierGroupID
        ),
        selectedSuppliers: (selectors.selectedSuppliers || []).map(
          (s) => s.s_ID ?? s.S_ID
        ),
        selectedAllergens: (selectors.selectedAllergens || []).map(
          (a) => a.allergen_ID ?? a.allergen_Name
        ),
        selectedMenuGroups: (selectors.selectedMenuGroups || []).map(
          (g) => g.menuGroupID ?? g.MenuGroupID
        ),
        selectedProducts: (selectors.selectedProducts || []).map((p) => ({
          id: p.menuItem_ID,
          qty: p.availableQuantity,
        })),
      },
      reportingCategoryId:
        reportingCategory?.rootCat_ID ?? reportingCategory?.rC_ID ?? null,
      expiryDate: expiryDate
        ? typeof expiryDate.toISOString === "function"
          ? expiryDate.toISOString()
          : String(expiryDate)
        : null,
      variants: (variants || []).map((v) => ({
        menuItem_ID: v.menuItem_ID || 0,
        menuItem_Name: v.menuItem_Name || "",
        menuItem_SalePrice: v.menuItem_SalePrice ?? "",
        menuItem_CostPrice: v.menuItem_CostPrice ?? "",
        menuItem_TaxPercentage: v.menuItem_TaxPercentage ?? "",
        menuItem_DeliveryPrice: v.menuItem_DeliveryPrice ?? "",
        menuItem_TakeAwayPrice: v.menuItem_TakeAwayPrice ?? "",
        menuItem_IsDefaultVariant: Boolean(v.menuItem_IsDefaultVariant),
        menuItem_SKU: v.menuItem_SKU || "",
        menuItem_ServingSize: v.menuItem_ServingSize || "",
        current_Quantity: v.current_Quantity ?? "",
        reorder_Quantity: v.reorder_Quantity ?? "",
        reorder_Level: v.reorder_Level ?? "",
      })),
    });
  }, [
    formState,
    toggles,
    imageState.menuItem_Image,
    imageState.imageFile,
    selectors,
    reportingCategory,
    expiryDate,
    variants,
  ]);

  useEffect(() => {
    getFormSnapshotRef.current = getFormSnapshot;
  }, [getFormSnapshot]);

  // Capture dirty baseline only after item + catalog load AND post-load sync
  // effects (tax defaults, allergen resolve, reporting/serving sync) settle.
  // Taking the snapshot too early marks the page dirty with no user edits.
  useEffect(() => {
    if (initialLoading || !catalogReady || !formState.menuItem_Name) {
      setBaselineReady(false);
      setInitialLoadedDataSnapshot(null);
      return undefined;
    }

    if (baselineReady) return undefined;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      setInitialLoadedDataSnapshot(getFormSnapshotRef.current());
      setBaselineReady(true);
    }, 100);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    initialLoading,
    catalogReady,
    formState.menuItem_Name,
    getFormSnapshot,
    baselineReady,
  ]);

  const isDirty =
    baselineReady &&
    !initialLoading &&
    initialLoadedDataSnapshot !== null &&
    getFormSnapshot() !== initialLoadedDataSnapshot;

  const handleUpdate = async (options = {}) => {
    const { exitAfterSave = true } = options;
    if (!formState.menuItem_Name.trim()) {
      setErrors((prev) => ({ ...prev, menuItem_Name: "Name is required" }));
      showErrorToast("Name is required.");
      return false;
    }
    if (toggles.menuItem_HasVariant) {
      const validVariants = variants.filter((v) => v.menuItem_Name.trim());
      if (validVariants.length === 0) {
        showErrorToast(
          "At least one variant is required when variants are enabled."
        );
        return false;
      }
      const defaultVariant = validVariants.find(
        (v) => v.menuItem_IsDefaultVariant
      );
      if (!defaultVariant) {
        showErrorToast("At least one variant must be marked as default.");
        return false;
      }
      for (const variant of validVariants) {
        if (parseFloat(variant.menuItem_SalePrice) <= 0) {
          showErrorToast("Variant Sale Price must be a positive number.");
          return false;
        }
      }
    } else {
      if (parseFloat(formState.menuItem_SalePrice) <= 0) {
        setErrors((prev) => ({
          ...prev,
          menuItem_SalePrice: "Sale Price must be a positive number",
        }));
        showErrorToast("Sale Price must be a positive number.");
        return false;
      }
    }
    if (selectors.selectedParentCategory.length === 0) {
      showErrorToast("At least one category must be selected.");
      return false;
    }
    if (!reportingCategory?.rootCat_ID && !reportingCategory?.rC_ID) {
      showErrorToast("Reporting Category is required.");
      return false;
    }
    if (
      formState.menuItem_Type === "Combo" &&
      toggles.productsEnabled &&
      selectors.selectedProducts.length === 0
    ) {
      showErrorToast("At least one product must be selected for a combo item.");
      return false;
    }
    if (
      formState.menuItem_Type === "Combo" &&
      toggles.productsEnabled &&
      selectors.selectedProducts.some(
        (product) =>
          !product.availableQuantity || !/^\d+$/.test(product.availableQuantity)
      )
    ) {
      showErrorToast("All selected products must have a valid quantity.");
      return false;
    }
    if (
      toggles.allergensEnabled &&
      selectors.selectedAllergens.length === 0
    ) {
      showErrorToast("At least one allergen must be selected when allergens are enabled.");
      return false;
    }
    setLoading(true);
    let uploadedImageUrl = imageState.menuItem_Image;
    try {
      if (imageState.imageFile) {
        const imageFormData = new FormData();
        imageFormData.append("ImageFile", imageState.imageFile);
        const imageResponse = await axiosInstance.post(
          APIs.POST_UPLOAD_PRODUCT_IMAGE,
          imageFormData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        uploadedImageUrl =
          imageResponse.data?.data?.filePath || imageState.menuItem_Image;
      }
    } catch (err) {
      showErrorToast("Failed to upload image");
    }
    try {
      const defaultVariant =
        (variants || []).find((v) => v.menuItem_IsDefaultVariant) ||
        (variants || [])[0];
      const parentName = (formState.menuItem_Name || "").trim();
      // Parent stores its own name; each variant stores "Parent (Variant)"
      const defaultVariantTitle = toggles.menuItem_HasVariant ? parentName : "";
      const payload = {
        menuItem_ID: parseInt(itemId),
        localizeID: formState.localizeID,
        compID: CompID,
        menuItem_Name: formState.menuItem_Name,
        menuItem_KitchenDisplayName: formState.menuItem_KitchenDisplayName,
        menuItem_Detail: formState.menuItem_Detail,
        menuItem_SalePrice: toggles.menuItem_HasVariant
          ? parseFloat(defaultVariant?.menuItem_SalePrice) || 0
          : parseFloat(formState.menuItem_SalePrice) || 0,
        menuItem_Image: uploadedImageUrl || "",
        menuItem_Publish: toggles.menuItem_Publish,
        menuItem_DataCreated: new Date().toISOString(),
        menuItem_ExpiryDate: toggles.menuItem_HasVariant
          ? null
          : expiryDate
            ? expiryDate.toISOString()
            : null,
        menuItem_DateModified: new Date().toISOString(),
        menuItem_DeliveryPrice: toggles.menuItem_HasVariant
          ? parseFloat(defaultVariant?.menuItem_DeliveryPrice) || 0
          : parseFloat(formState.menuItem_DeliveryPrice) || 0,
        menuItem_TakeAwayPrice: toggles.menuItem_HasVariant
          ? parseFloat(defaultVariant?.menuItem_TakeAwayPrice) || 0
          : parseFloat(formState.menuItem_TakeAwayPrice) || 0,
        menuItem_CostPrice: toggles.menuItem_HasVariant
          ? toggles.isTaxExempt
            ? 0
            : parseFloat(defaultVariant?.menuItem_CostPrice) || 0
          : toggles.isTaxExempt
            ? 0
            : parseFloat(formState.menuItem_CostPrice) || 0,
        menuItem_TaxPercentage: toggles.menuItem_HasVariant
          ? parseFloat(defaultVariant?.menuItem_TaxPercentage) || 0
          : toggles.isTaxExempt
            ? 0
            : parseFloat(formState.menuItem_TaxPercentage) || 0,
        menuItem_SKU: toggles.skuEnabled ? formState.menuItem_SKU : "",
        menuItem_RootType: formState.menuItem_RootType,
        menuItem_SEOTitle: toggles.seoEnabled
          ? formState.menuItem_SEOTitle
          : "",
        menuItem_SEODescription: toggles.seoEnabled
          ? formState.menuItem_SEODescription
          : "",
        menuItem_Permalink: toggles.seoEnabled
          ? formState.menuItem_Permalink
          : "",
        isActiveMobileApp: toggles.isActiveMobileApp,
        is_OutOfStock: toggles.is_OutOfStock,
        isSellOnTill: toggles.isSellOnTill,
        is_ShowInKiosk: toggles.is_ShowInKiosk,
        hasAllergen:
          toggles.allergensEnabled && selectors.selectedAllergens.length > 0,
        menuItem_HaveModifier:
          toggles.modifierGroupsEnabled &&
          selectors.selectedModifierGroups.length > 0,
        categories: selectors.selectedParentCategory,
        suppliers: toggles.suppliersEnabled ? selectors.selectedSuppliers : [],
        modifierGroups: toggles.modifierGroupsEnabled
          ? selectors.selectedModifierGroups
          : [],
        menuGroups: selectors.selectedMenuGroups,
        allergens:
          toggles.allergensEnabled && selectors.selectedAllergens.length > 0
            ? selectors.selectedAllergens.map((a) => a.allergen_Name).join(", ")
            : "",
        allergenSolutions:
          toggles.allergensEnabled && selectors.selectedAllergens.length > 0
            ? joinAllergenSolutions(selectors.selectedAllergens)
            : "",
        menuItem_PreparationTime: formState.menuItem_PreparationTime.trim(),
        menuItem_ServingSize: formState.menuItem_ServingSize.trim(),
        menuItem_CaloriesPerServing: formState.menuItem_CaloriesPerServing
          ? parseInt(formState.menuItem_CaloriesPerServing, 10)
          : null,
        menuItem_ASTime: formState.menuItem_ASTime || "",
        menuItem_AETime: formState.menuItem_AETime || "",
        menuItem_Rating: parseInt(formState.menuItem_Rating) || 0,
        menuItem_Homefeatured: toggles.menuItem_Homefeatured,
        menuItem_featured: toggles.menuItem_featured,
        menuItem_IsModifier: toggles.menuItem_IsModifier,
        menuItem_ModifierType: toggles.menuItem_IsModifier
          ? formState.menuItem_ModifierType || "0"
          : "0",
        menuItem_IsFavorite: toggles.menuItem_IsFavorite,
        menuItem_BarCode: formState.menuItem_BarCode || "",
        menuItem_Type: formState.menuItem_Type,
        menuItem_ColorCode: formState.menuItem_ColorCode || "",
        retail_Markup: toggles.menuItem_HasVariant
          ? 0
          : parseFloat(formState.retail_Markup) || 0,
        delivery_Markup: toggles.menuItem_HasVariant
          ? 0
          : parseFloat(formState.delivery_Markup) || 0,
        online_Markup: toggles.menuItem_HasVariant
          ? 0
          : parseFloat(formState.online_Markup) || 0,
        current_Quantity: toggles.menuItem_HasVariant
          ? 0
          : parseFloat(formState.current_Quantity) || 0,
        reorder_Quantity: toggles.menuItem_HasVariant
          ? 0
          : parseFloat(formState.reorder_Quantity) || 0,
        reorder_Level: toggles.menuItem_HasVariant
          ? 0
          : parseFloat(formState.reorder_Level) || 0,
        availableQuantity: toggles.menuItem_HasVariant
          ? null
          : parseFloat(formState.availableQuantity) || null,
        orderQuantityLimit: toggles.menuItem_HasVariant
          ? 0
          : parseInt(formState.orderQuantityLimit) || 0,
        brand_ID: parseInt(formState.brand_ID) || 0,
        ingredientID: parseInt(formState.ingredientID) || 0,
        parentMenuItemID: parseInt(formState.parentMenuItemID) || 0,
        itsMasterProductID: parseInt(formState.itsMasterProductID) || 0,
        reportCat_ID: (reportingCategory?.rootCat_ID || reportingCategory?.rC_ID)
          ? parseInt(reportingCategory.rootCat_ID || reportingCategory.rC_ID)
          : 0,
        stadardUnit: toggles.menuItem_HasVariant
          ? 0
          : parseInt(formState.stadardUnit) || 0,
        unitSize: toggles.menuItem_HasVariant
          ? 0
          : parseInt(formState.unitSize) || 0,
        mP_UnitOfSale: formState.mP_UnitOfSale || "",
        mP_VolumnOfSale: toggles.menuItem_HasVariant
          ? 0
          : parseFloat(formState.mP_VolumnOfSale) || 0,
        menuItem_HasVariant: toggles.menuItem_HasVariant,
        isArchieve: toggles.isArchieve,
        isAllowedtoSetPriceatThePointofSale:
          toggles.isAllowedtoSetPriceatThePointofSale,
        isTaxExempt: toggles.isTaxExempt,
        isPreventGainingLoyalty: toggles.isPreventGainingLoyalty,
        isHaveCustomerSpecificPricing: toggles.isHaveCustomerSpecificPricing,
        isProductisScannableOnly: toggles.isProductisScannableOnly,
        isMasterProduct: toggles.isMasterProduct,
        itHasAMasterProduct: toggles.itHasAMasterProduct,
        menuItem_IsDefaultModifier: toggles.menuItem_IsDefaultModifier,
        menuItem_DefaultModifierTitle:
          defaultVariant?.menuItem_DefaultModifierTitle || "",
        isShowNotificationExpDate: toggles.isShowNotificationExpDate,
        isShowSocialMediaLink: toggles.isShowSocialMediaLink,
        IsSpecialItem: toggles.IsSpecialItem,
        menuItem_ImageAltText: formState.menuItem_ImageAltText || "",
        appMode: toggles.menuItem_HasVariant ? "" : formState.appMode,
        menuItem_IsVariant: false,
        menuItem_IsDefaultVariant: false,
        menuItem_DefaultVariantTitle: defaultVariantTitle,
        menuitem_VariantParentID: parseInt(formState.parentMenuItemID) || 0,
        Products:
          formState.menuItem_Type === "Combo"
            ? selectors.selectedProducts
            : toggles.menuItem_HasVariant
              ? variants
                .filter((v) => v.menuItem_Name && v.menuItem_Name.trim().length > 0)
                .map((v) => {
                  const { __taxEdited, ...rest } = v;
                  const taxVal = rest.menuItem_TaxPercentage;
                  const parsedTax = parseFloat(taxVal);
                  const menuItem_TaxPercentage =
                    taxVal === "" || taxVal == null
                      ? null
                      : Number.isFinite(parsedTax)
                        ? parsedTax
                        : null;
                  const rawExp = v.menuItem_ExpiryDate;
                  const menuItem_ExpiryDate =
                    !rawExp || rawExp === "0001-01-01T00:00:00"
                      ? null
                      : typeof rawExp === "string"
                        ? rawExp
                        : rawExp instanceof Date
                          ? rawExp.toISOString()
                          : null;
                  const variantName = (v.menuItem_Name || "").trim();
                  return {
                    ...rest,
                    reportCat_ID: (reportingCategory?.rootCat_ID || reportingCategory?.rC_ID)
                      ? parseInt(reportingCategory.rootCat_ID || reportingCategory.rC_ID)
                      : 0,
                    menuItem_ServingSize: v.menuItem_ServingSize || "",
                    menuItem_CaloriesPerServing: v.menuItem_CaloriesPerServing
                      ? parseInt(v.menuItem_CaloriesPerServing, 10)
                      : null,
                    menuItem_ExpiryDate,
                    hasAllergen: false,
                    allergens: "",
                    allergenSolutions: "",
                    appMode: "",
                    menuItem_SKU: v.menuItem_SKU || "",
                    current_Quantity: parseFloat(v.current_Quantity) || 0,
                    reorder_Quantity: parseFloat(v.reorder_Quantity) || 0,
                    reorder_Level: parseFloat(v.reorder_Level) || 0,
                    availableQuantity:
                      v.availableQuantity != null && v.availableQuantity !== ""
                        ? parseFloat(v.availableQuantity)
                        : null,
                    menuItem_TaxPercentage,
                    // Variants store "Parent (Variant)" for display / reporting
                    menuItem_DefaultVariantTitle:
                      parentName && variantName
                        ? `${parentName} (${variantName})`
                        : "",
                  };
                })
              : [],
      };
      const response = await axiosInstance.put(APIs.PUT_UPDATE_ITEM, payload);
      if (response.data.status) {
        showSuccessToast("Item updated successfully");
        setInitialLoadedDataSnapshot(getFormSnapshot());
        if (exitAfterSave) {
          navigate("/items/manageItems");
        }
        return true;
      } else {
        showErrorToast(response.data.message || "Failed to update item");
        return false;
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to update item");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const variantModalSectionStyle = {
    backgroundColor: isDarkMode ? "#1a202c" : "#f8f9fa",
    border: isDarkMode ? "1px solid #4a5568" : "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "16px",
  };

  const renderVariantDetailModal = () => {
    if (!variantDetailDraft) return null;
    const d = variantDetailDraft;
    return (
      <CustomModal
        title="Variant details"
        subtitle={
          d.menuItem_Name?.trim()
            ? `Editing: ${d.menuItem_Name.trim()}`
            : "Edit pricing, SKU, expiry, and stock"
        }
        fontSize="18px"
        fontWeight={600}
        isOpen={variantDetailModalOpen}
        onClose={closeVariantDetailModal}
        onSubmit={saveVariantDetailModal}
        showButton={true}
        showCancelButton={true}
        showSubmitButton={true}
        submitButtonText="Save"
        isCentered={true}
        modalSize="modal-lg"
        maxHeight="90vh"
        minHeight="360px"
      >
        <div className="container-fluid px-0">
          <div style={variantModalSectionStyle}>
            <h6
              className="fw-semibold mb-3"
              style={{ color: isDarkMode ? "#ffffff" : "#2d3748" }}
            >
              Variant
            </h6>
            <div className="row g-3">
              <div className="col-md-6">
                <FormInput
                  label="Variant name"
                  type="text"
                  value={d.menuItem_Name || ""}
                  placeHolder="Enter variant name"
                  onchange={(e) =>
                    updateVariantDetailDraft("menuItem_Name", e.target.value)
                  }
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Kitchen name"
                  type="text"
                  value={d.menuItem_KitchenDisplayName || ""}
                  placeHolder="Kitchen display name"
                  onchange={(e) =>
                    updateVariantDetailDraft(
                      "menuItem_KitchenDisplayName",
                      e.target.value
                    )
                  }
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Serving size"
                  type="text"
                  value={d.menuItem_ServingSize || ""}
                  placeHolder="e.g. 250g or 2 slices"
                  onchange={(e) =>
                    updateVariantDetailDraft(
                      "menuItem_ServingSize",
                      e.target.value
                    )
                  }
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Calories per serving"
                  type="number"
                  value={d.menuItem_CaloriesPerServing ?? ""}
                  placeHolder="e.g. 450"
                  onchange={(e) =>
                    updateVariantDetailDraft(
                      "menuItem_CaloriesPerServing",
                      e.target.value
                    )
                  }
                />
              </div>
              <div className="col-md-6 d-flex align-items-end">
                <ToggleSwitch
                  label="Published"
                  isChecked={!!d.menuItem_Publish}
                  onToggle={setVariantDetailPublish}
                  themeColor={themeColor}
                />
              </div>
            </div>
          </div>

          <div style={variantModalSectionStyle}>
            <h6
              className="fw-semibold mb-3"
              style={{ color: isDarkMode ? "#ffffff" : "#2d3748" }}
            >
              Pricing
            </h6>
            <div className="row g-3">
              <div className="col-md-4">
                <FormInput
                  label={`Cost price (${getCurrencySymbol(compCurrency)})`}
                  type="number"
                  value={
                    d.menuItem_CostPrice !== undefined &&
                    d.menuItem_CostPrice !== null &&
                    d.menuItem_CostPrice !== ""
                      ? String(d.menuItem_CostPrice)
                      : ""
                  }
                  placeHolder="0.00"
                  step="0.01"
                  onchange={(e) =>
                    updateVariantDetailDraft("menuItem_CostPrice", e.target.value)
                  }
                />
              </div>
              <div className="col-md-4">
                <FormInput
                  label="Tax %"
                  type="number"
                  value={
                    d.menuItem_TaxPercentage == null
                      ? ""
                      : String(d.menuItem_TaxPercentage)
                  }
                  placeHolder={
                    settingsData?.isTaxEnabled && settingsData?.s_VAT && !toggles.isTaxExempt
                      ? settingsData.s_VAT.toString()
                      : "0"
                  }
                  step="0.01"
                  readOnly={toggles.isTaxExempt}
                  onchange={(e) =>
                    updateVariantDetailDraft(
                      "menuItem_TaxPercentage",
                      e.target.value
                    )
                  }
                />
              </div>
              <div className="col-md-4">
                <FormInput
                  label={`Sale price (${getCurrencySymbol(compCurrency)})`}
                  type="number"
                  value={
                    d.menuItem_SalePrice != null &&
                    d.menuItem_SalePrice !== "" &&
                    d.menuItem_SalePrice !== 0 &&
                    d.menuItem_SalePrice !== "0"
                      ? String(d.menuItem_SalePrice)
                      : ""
                  }
                  placeHolder="0.00"
                  step="0.01"
                  onchange={(e) =>
                    updateVariantDetailDraft(
                      "menuItem_SalePrice",
                      e.target.value
                    )
                  }
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label={`Delivery price (${getCurrencySymbol(compCurrency)})`}
                  type="number"
                  value={
                    d.menuItem_DeliveryPrice != null &&
                    d.menuItem_DeliveryPrice !== "" &&
                    d.menuItem_DeliveryPrice !== 0 &&
                    d.menuItem_DeliveryPrice !== "0"
                      ? String(d.menuItem_DeliveryPrice)
                      : ""
                  }
                  placeHolder="0.00"
                  step="0.01"
                  onchange={(e) =>
                    updateVariantDetailDraft(
                      "menuItem_DeliveryPrice",
                      e.target.value
                    )
                  }
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label={`Takeaway price (${getCurrencySymbol(compCurrency)})`}
                  type="number"
                  value={
                    d.menuItem_TakeAwayPrice != null &&
                    d.menuItem_TakeAwayPrice !== "" &&
                    d.menuItem_TakeAwayPrice !== 0 &&
                    d.menuItem_TakeAwayPrice !== "0"
                      ? String(d.menuItem_TakeAwayPrice)
                      : ""
                  }
                  placeHolder="0.00"
                  step="0.01"
                  onchange={(e) =>
                    updateVariantDetailDraft(
                      "menuItem_TakeAwayPrice",
                      e.target.value
                    )
                  }
                />
              </div>
            </div>
          </div>

          <div style={variantModalSectionStyle}>
            <h6
              className="fw-semibold mb-3"
              style={{ color: isDarkMode ? "#ffffff" : "#2d3748" }}
            >
              SKU and expiry
            </h6>
            <div className="row g-3 align-items-start">
              <div className="col-md-6 d-flex flex-column">
                <label
                  className="form-label fw-semibold mb-2"
                  style={{ color: isDarkMode ? "#f1f5f9" : "#2d3748" }}
                >
                  SKU
                </label>
                <FormInput
                  type="text"
                  value={d.menuItem_SKU || ""}
                  placeHolder="SKU code"
                  onchange={(e) =>
                    updateVariantDetailDraft("menuItem_SKU", e.target.value)
                  }
                />
              </div>
              <div className="col-md-6 d-flex flex-column">
                <label
                  className="form-label fw-semibold mb-2"
                  style={{ color: isDarkMode ? "#f1f5f9" : "#2d3748" }}
                >
                  Expiry date
                </label>
                <div style={{ width: "100%", minHeight: "44px" }}>
                  <DebugMUIDatePicker
                    value={variantDetailExpiry}
                    onChange={(date) => setVariantDetailExpiry(date)}
                    placeholder="Select expiry date"
                    name="variantDetailExpiry"
                    clearable={true}
                    style={{ width: "100%" }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={{ ...variantModalSectionStyle, marginBottom: 0 }}>
            <h6
              className="fw-semibold mb-3"
              style={{ color: isDarkMode ? "#ffffff" : "#2d3748" }}
            >
              Inventory
            </h6>
            <div className="row g-3">
              <div className="col-md-6">
                <FormInput
                  label="Current quantity"
                  type="number"
                  value={d.current_Quantity ?? ""}
                  placeHolder="0"
                  onchange={(e) =>
                    updateVariantDetailDraft(
                      "current_Quantity",
                      e.target.value
                    )
                  }
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Reorder quantity"
                  type="number"
                  value={d.reorder_Quantity ?? ""}
                  placeHolder="0"
                  onchange={(e) =>
                    updateVariantDetailDraft(
                      "reorder_Quantity",
                      e.target.value
                    )
                  }
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Reorder level"
                  type="number"
                  value={d.reorder_Level ?? ""}
                  placeHolder="0"
                  onchange={(e) =>
                    updateVariantDetailDraft("reorder_Level", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="mt-3">
              <ToggleSwitch
                label="Default variant"
                isChecked={!!d.menuItem_IsDefaultVariant}
                onToggle={(checked) =>
                  updateVariantDetailDraft(
                    "menuItem_IsDefaultVariant",
                    checked
                  )
                }
                themeColor={themeColor}
              />
            </div>
          </div>
        </div>
      </CustomModal>
    );
  };

  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="#/">Home</a>
          </li>
          <li className="breadcrumb-item">
            <Link to="/items/manageItems">Manage Items</Link>
          </li>
          <li className="breadcrumb-item active">Update Item</li>
        </ol>
        <h1 className="page-header mb-0">
          {formState.menuItem_Type === "Combo"
            ? "Update Combo Item"
            : "Update Item"}
        </h1>
      </div>
      <SaveActions
        onCancel={() => navigate("/items/manageItems")}
        onSave={() => handleUpdate({ exitAfterSave: false })}
        onSaveAndExit={() => handleUpdate({ exitAfterSave: true })}
        saving={loading}
        disabled={loading || initialLoading}
        savingLabel="Updating..."
      />
    </div>
  );

  const renderToggles = () => (
    <div className="row row-cols-2 g-3">
      {[
        { label: "Active", field: "menuItem_Publish" },
        { label: "Show in Kiosk", field: "is_ShowInKiosk" },
        { label: "Show in POS", field: "isSellOnTill" },
        { label: "Tax Exempt", field: "isTaxExempt" },
        { label: "Show in Online", field: "isActiveMobileApp" },
        { label: "Out of Stock", field: "is_OutOfStock" },
      ].map(({ label, field }) => (
        <div key={field} className="col">
          <ToggleSwitch
            isChecked={toggles[field]}
            onToggle={handleToggle(field)}
            label={label}
            name={field}
            themeColor={themeColor}
          />
        </div>
      ))}
    </div>
  );

  const renderFormFields = () => (
    <div className="row g-4">
      <div className="col-md-6">
        <FormInput
          label={
            formState.menuItem_Type === "Combo" ? "Combo Name *" : "Item Name *"
          }
          value={formState.menuItem_Name}
          onchange={handleInputChange("menuItem_Name")}
          type="text"
          placeHolder="Name"
          name="menuItem_Name"
          required
          error={errors.menuItem_Name}
        />
        <FormInput
          label={
            formState.menuItem_Type === "Combo"
              ? "Combo Kitchen Name"
              : "Kitchen Name"
          }
          value={formState.menuItem_KitchenDisplayName}
          onchange={handleInputChange("menuItem_KitchenDisplayName")}
          type="text"
          placeHolder="Kitchen Name"
          name="menuItem_KitchenDisplayName"
        />
        <div className="mb-4">
          <label className="form-label fw-medium">
            <b>
              {formState.menuItem_Type === "Combo"
                ? "Combo Description"
                : "Item Description"}
            </b>
          </label>
          <textarea
            className={`form-control ${isDarkMode ? 'dark-mode-input' : 'light-mode-input'}`}
            rows={6}
            placeholder="Description (max 500 characters)"
            value={formState.menuItem_Detail}
            onChange={handleInputChange("menuItem_Detail")}
            style={{
              borderRadius: "10px",
              border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
              background: isDarkMode ? "#2d3436" : "#F9F9F9",
              color: isDarkMode ? "#ffffff" : "#000000",
              transition: "border-color 0.2s ease, box-shadow 0.2s ease",
            }}
          />
        </div>
        {formState.menuItem_Type !== "Combo" && (
          <div className="mb-4">
            <ToggleSwitch
              isChecked={toggles.menuItem_HasVariant}
              onToggle={handleToggle("menuItem_HasVariant")}
              label="Is Product have multiple variants"
              name="menuItem_HasVariant"
              themeColor={themeColor}
            />
          </div>
        )}
        {!toggles.menuItem_HasVariant && (
          <div 
            className="card p-3 mb-4"
            style={{
              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
              border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
              color: isDarkMode ? '#ffffff' : '#000000',
            }}
          >
            <h5 className="card-title mb-3">Pricing</h5>
            <div className="row g-3">
              <div className="col-6">
                <FormInput
                  label={`Cost Price (${getCurrencySymbol(compCurrency)})`}
                  value={formState.menuItem_CostPrice}
                  onchange={handleInputChange("menuItem_CostPrice")}
                  type="number"
                  placeHolder="0.00"
                  name="menuItem_CostPrice"
                />
              </div>
              <div className="col-6">
                <FormInput
                  label="Tax (Percentage)"
                  value={formState.menuItem_TaxPercentage}
                  onchange={handleInputChange("menuItem_TaxPercentage")}
                  type="text"
                  placeHolder="0.00"
                  name="menuItem_TaxPercentage"
                  disabled={toggles.isTaxExempt}
                  title={
                    formState.menuItem_TaxPercentage !== "" &&
                    settingsData?.s_VAT !== undefined &&
                    settingsData?.s_VAT !== null &&
                    settingsData?.s_VAT !== "" &&
                    formState.menuItem_TaxPercentage === String(settingsData.s_VAT)
                      ? "Default tax rate from settings - you can edit this value"
                      : "Enter tax percentage"
                  }
                />
                {formState.menuItem_TaxPercentage !== "" &&
                settingsData?.s_VAT !== undefined &&
                settingsData?.s_VAT !== null &&
                settingsData?.s_VAT !== "" &&
                formState.menuItem_TaxPercentage === String(settingsData.s_VAT) ? (
                  <small className="text-muted">
                    <i className="fa fa-info-circle me-1"></i>
                    Default from settings - you can edit this value
                  </small>
                ) : null}
              </div>
              <div className="col-6">
                <FormInput
                  label={`Sale Price * (${getCurrencySymbol(compCurrency)})`}
                  value={formState.menuItem_SalePrice}
                  onchange={handleInputChange("menuItem_SalePrice")}
                  type="number"
                  placeHolder="0.00"
                  name="menuItem_SalePrice"
                  required
                  error={errors.menuItem_SalePrice}
                />
              </div>
            </div>
            <h5 className="card-title mt-3">Additional Pricing</h5>
            <div className="row g-3 mt-2">
              <div className="col-6">
                <FormInput
                  label={`Takeaway Price (${getCurrencySymbol(compCurrency)})`}
                  value={formState.menuItem_TakeAwayPrice}
                  onchange={handleInputChange("menuItem_TakeAwayPrice")}
                  type="number"
                  placeHolder="0.00"
                  name="menuItem_TakeAwayPrice"
                />
              </div>
              <div className="col-6">
                <FormInput
                  label={`Delivery Price (${getCurrencySymbol(compCurrency)})`}
                  value={formState.menuItem_DeliveryPrice}
                  onchange={handleInputChange("menuItem_DeliveryPrice")}
                  type="number"
                  placeHolder="0.00"
                  name="menuItem_DeliveryPrice"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="col-md-6">
        <div className="row mb-2">
          <div className="col-md-12">
            <FormInput
              label="Product Type"
              value={formState.menuItem_RootType}
              onchange={handleInputChange("menuItem_RootType")}
              type="text"
              placeHolder="Select Product Type"
              name="menuItem_RootType"
              icon="fa fa-caret-down"
              iconOnClick={() =>
                setModals((prev) => ({ ...prev, isModalOpen: true }))
              }
              readOnly
            />
          </div>
          {/* <div className="col-md-12">
            <FormInput
              label="Color Code"
              value={formState.menuItem_ColorCode}
              onchange={handleInputChange("menuItem_ColorCode")}
              type="color"
              name="menuItem_ColorCode"
            />
          </div> */}
        </div>
      <div className="row g-3 mb-3">
          <div className={toggles.menuItem_HasVariant ? "col-12" : "col-6"}>
            <FormInput
              label="Preparation Time"
              value={formState.menuItem_PreparationTime}
              onchange={handleInputChange("menuItem_PreparationTime")}
              type="text"
              placeHolder="00:00"
              name="menuItem_PreparationTime"
            />
          </div>
          {!toggles.menuItem_HasVariant && (
            <>
              <div className="col-6">
                <FormInput
                  label="Serving Size"
                  value={formState.menuItem_ServingSize}
                  onchange={handleInputChange("menuItem_ServingSize")}
                  type="text"
                  placeHolder="e.g. 250g or 2 slices"
                  name="menuItem_ServingSize"
                />
              </div>
              <div className="col-6">
                <FormInput
                  label="Calories per serving"
                  value={formState.menuItem_CaloriesPerServing}
                  onchange={handleInputChange("menuItem_CaloriesPerServing")}
                  type="number"
                  placeHolder="e.g. 450"
                  name="menuItem_CaloriesPerServing"
                />
              </div>
            </>
          )}
        </div>
        {!toggles.menuItem_HasVariant && (
          <div className="col-12 mb-3">
            <div 
              className="card p-3 mb-4"
              style={{
                backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
                color: isDarkMode ? '#ffffff' : '#000000',
              }}
            >
              <h5 className="card-title mb-3">Inventory</h5>
              <div className="row g-3">
                <div className="col-6">
                  <FormInput
                    label="Current Quantity"
                    value={formState.current_Quantity}
                    onchange={handleInputChange("current_Quantity")}
                    type="number"
                    placeHolder="0"
                    name="current_Quantity"
                  />
                </div>
                <div className="col-6">
                  <FormInput
                    label="Reorder Quantity"
                    value={formState.reorder_Quantity}
                    onchange={handleInputChange("reorder_Quantity")}
                    type="number"
                    placeHolder="0"
                    name="reorder_Quantity"
                  />
                </div>
                <div className="col-6">
                  <FormInput
                    label="Reorder Level"
                    value={formState.reorder_Level}
                    onchange={handleInputChange("reorder_Level")}
                    type="number"
                    placeHolder="0"
                    name="reorder_Level"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {!isComboItem && toggles.menuItem_HasVariant && (
        <div className="row">
          <div className="col-12 mt-4">
            <div 
              className="card p-3 mb-4"
              style={{
                backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
                color: isDarkMode ? '#ffffff' : '#000000',
              }}
            >
              <h5 className="card-title mb-1">Variants</h5>
              <p
                className="small mb-3"
                style={{ color: isDarkMode ? "#a0aec0" : "#6c757d" }}
              >
                Click a row (outside inputs and actions) to edit SKU, expiry,
                inventory, and full pricing in a modal.
              </p>
              <table className="table table-responsive">
                <thead style={{ 
                  backgroundColor: isDarkMode ? "#404040" : "#d9d9d9",
                  color: isDarkMode ? "#ffffff" : "#000000"
                }}>
                  <tr>
                    <th style={{ width: "42px" }}></th>
                    <th>Variant</th>
                    <th>Kitchen Name</th>
                    <th>Serving Size</th>
                    <th>{`Cost Price (${getCurrencySymbol(compCurrency)})`}</th>
                    <th>Tax %</th>
                    <th>{`Sale Price (${getCurrencySymbol(compCurrency)})`}</th>
                    <th>{`Delivery Price (${getCurrencySymbol(compCurrency)})`}</th>
                    <th>{`Takeaway Price (${getCurrencySymbol(compCurrency)})`}</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(variants || []).map((variant, index) => (
                    <tr
                      key={index}
                      style={{ cursor: "pointer" }}
                      draggable
                      onClick={(e) => handleVariantRowClick(e, index)}
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnter={(e) => handleDragEnter(e, index)}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                    >
                      <td className="align-middle text-center">
                        <i
                          className="fa fa-bars"
                          style={{ cursor: "grab", color: isDarkMode ? "#e2e8f0" : "#6c757d" }}
                          title="Drag to reorder"
                        ></i>
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="text"
                          value={variant.menuItem_Name || ""}
                          placeHolder="Enter Variant Name"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_Name",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="text"
                          value={variant.menuItem_KitchenDisplayName || ""}
                          placeHolder="Enter Kitchen Name"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_KitchenDisplayName",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="text"
                          value={variant.menuItem_ServingSize || ""}
                          placeHolder="1"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_ServingSize",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="number"
                          value={
                            variant.menuItem_CostPrice !== undefined &&
                            variant.menuItem_CostPrice !== null &&
                            variant.menuItem_CostPrice !== ""
                              ? String(variant.menuItem_CostPrice)
                              : ""
                          }
                          placeHolder="0.00"
                          step="0.01"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_CostPrice",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="number"
                          value={
                            variant.menuItem_TaxPercentage == null
                              ? ""
                              : String(variant.menuItem_TaxPercentage)
                          }
                          placeHolder={settingsData?.isTaxEnabled && settingsData?.s_VAT && !toggles.isTaxExempt
                            ? settingsData.s_VAT.toString()
                            : "0.00"}
                          step="0.01"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_TaxPercentage",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="number"
                          value={(variant.menuItem_SalePrice && variant.menuItem_SalePrice !== "0" && variant.menuItem_SalePrice !== 0) ? variant.menuItem_SalePrice.toString() : ""}
                          placeHolder="0.00"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          step="0.01"
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_SalePrice",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="number"
                          value={(variant.menuItem_DeliveryPrice && variant.menuItem_DeliveryPrice !== "0" && variant.menuItem_DeliveryPrice !== 0) ? variant.menuItem_DeliveryPrice.toString() : ""}
                          placeHolder="0.00"
                          step="0.01"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_DeliveryPrice",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <FormInput
                          type="number"
                          value={(variant.menuItem_TakeAwayPrice && variant.menuItem_TakeAwayPrice !== "0" && variant.menuItem_TakeAwayPrice !== 0) ? variant.menuItem_TakeAwayPrice.toString() : ""}
                          placeHolder="0.00"
                          step="0.01"
                          draggable={false}
                          onDragStart={(e) => e.stopPropagation()}
                          onchange={(e) =>
                            handleVariantChange(
                              index,
                              "menuItem_TakeAwayPrice",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <div className="d-flex align-items-center m-2 me-3">
                          <ToggleSwitch
                            isChecked={variant.menuItem_IsDefaultVariant}
                            label="Default"
                            onToggle={() =>
                              handleVariantChange(
                                index,
                                "menuItem_IsDefaultVariant",
                                !variant.menuItem_IsDefaultVariant
                              )
                            }
                            themeColor={themeColor}
                          />
                        </div>
                      </td>
                      <td className="align-middle">
                        <div className="d-flex align-items-center m-1">
                          <i
                            className="fa fa-eye text-primary me-3"
                            style={{ cursor: "pointer" }}
                            title="Open variant details"
                            onClick={(e) => {
                              e.stopPropagation();
                              openVariantDetailModal(index);
                            }}
                          ></i>
                          <i
                            className="fa fa-trash text-danger m-3"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleRemoveVariant(index)}
                          ></i>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );

const renderAdditionalOptions = () => (
  <div className="row g-4 mt-2">
    <div className="col-12">
      <div 
        className="card p-3"
        style={{
          backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
          border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
          color: isDarkMode ? '#ffffff' : '#000000',
        }}
      >
        <h5 className="card-title mb-3">Additional Options</h5>
        <div className="row g-3 d-flex align-items-center p-4">
          <div className="col-2 d-flex">
            <ToggleSwitch
              isChecked={toggles.skuEnabled}
              onToggle={handleToggle("skuEnabled")}
              label="SKU Enable"
              name="skuEnabled"
              themeColor={themeColor}
            />
          </div>
          <div className="col-4 d-flex">
            {toggles.skuEnabled && (
              <FormInput
                placeHolder="SKU Code"
                type="text"
                value={formState.menuItem_SKU}
                onchange={handleInputChange("menuItem_SKU")}
                className="form-control flex-grow-1"
              />
            )}
          </div>
          <div className="col-2 d-flex">
            <ToggleSwitch
              isChecked={toggles.expiryDateEnabled}
              onToggle={handleToggle("expiryDateEnabled")}
              label="Expiry Date"
              name="expiryDateEnabled"
              themeColor={themeColor}
            />
          </div>
          <div className="col-4 d-flex">
            {toggles.expiryDateEnabled && (
              <div style={{ width: '100%' }}>
                {/* MUI-Style Date Picker */}
                <DebugMUIDatePicker        
                  value={expiryDate}
                  onChange={(date) => setExpiryDate(date)}
                  placeholder="Select expiry date"
                  name="expiryDate"
                  clearable={true}
                  minDate={new Date()} // Disable past dates
                />
                
                {/* Or use the existing CustomDateTimePicker - uncomment below and comment above */}
                {/* <CustomDateTimePicker
                  label="Expiry Date"
                  mode="date"
                  value={expiryDate}
                  onChange={(date) =>
                    setExpiryDate(date ? date.toDate() : null)
                  }
                  inputFormat="MM/DD/YYYY"
                  name="expiryDate"
                  isValidDate={disablePastDates}
                  placeholder="Select expiry date"
                  themeColor={themeColor}
                  components={{
                    OpenPickerIcon: () => (
                      <i
                        className="fa fa-calendar"
                        style={{ fontSize: "16px" }}
                      />
                    ),
                  }}
                  inputProps={{
                    className: `form-control ${isDarkMode ? 'dark-mode-input' : 'light-mode-input'}`,
                    style: {
                      border: isDarkMode ? '1px solid #404040' : '1px solid #D9D9D9',
                      background: isDarkMode ? '#2d3436' : '#F9F9F9',
                      color: isDarkMode ? '#ffffff' : '#000000',
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    },
                  }}
                /> */}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);
  const renderAdditionalToggles = () => (
    <div className="row g-4">
      <div className="col-12">
        <div 
          className="card p-3"
          style={{
            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
            border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
            color: isDarkMode ? '#ffffff' : '#000000',
          }}
        >
          <h5 className="card-title mb-3">Additional Flags</h5>
          <div className="row row-cols-3 g-3">
            <div className="col-6">
              <ToggleSwitch
                isChecked={toggles.isPreventGainingLoyalty}
                onToggle={handleToggle("isPreventGainingLoyalty")}
                label="Prevent Loyalty Points"
                name="isPreventGainingLoyalty"
                themeColor={themeColor}
              />
            </div>
            <div className="col-6">
              <ToggleSwitch
                isChecked={toggles.isShowNotificationExpDate}
                onToggle={handleToggle("isShowNotificationExpDate")}
                label="Show Expiry Notification"
                name="isShowNotificationExpDate"
                themeColor={themeColor}
              />
            </div>
            <div className="col-6">
              <ToggleSwitch
                isChecked={toggles.isArchieve}
                onToggle={handleToggle("isArchieve")}
                label="Is Archived"
                name="isArchieve"
                themeColor={themeColor}
              />
            </div>
            <div className="col-6">
              <ToggleSwitch
                isChecked={toggles.isAllowedtoSetPriceatThePointofSale}
                onToggle={handleToggle("isAllowedtoSetPriceatThePointofSale")}
                label="Set Price at POS"
                name="isAllowedtoSetPriceatThePointofSale"
                themeColor={themeColor}
              />
            </div>
            <div className="col-6">
              <ToggleSwitch
                isChecked={toggles.isShowSocialMediaLink}
                onToggle={handleToggle("isShowSocialMediaLink")}
                label="Show Social Media Link"
                name="isShowSocialMediaLink"
                themeColor={themeColor}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSEO = () => (
    <div className="col-12 mt-4">
      <div 
        className="card p-4"
        style={{
          backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
          border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
          color: isDarkMode ? '#ffffff' : '#000000',
        }}
      >
        <ToggleSwitch
          isChecked={toggles.seoEnabled}
          onToggle={handleToggle("seoEnabled")}
          label="Search Engine Optimization"
          name="seoEnabled"
          themeColor={themeColor}
        />
        {toggles.seoEnabled && (
          <div className="mt-3">
            <div className="row g-3">
              <div className="col-6">
                <FormInput
                  label="SEO Title"
                  type="text"
                  placeHolder="Enter SEO Title"
                  name="menuItem_SEOTitle"
                  value={formState.menuItem_SEOTitle}
                  onchange={handleInputChange("menuItem_SEOTitle")}
                />
              </div>
              <div className="col-6">
                <FormInput
                  label="Permalink"
                  type="text"
                  placeHolder="Enter permalink"
                  name="menuItem_Permalink"
                  value={formState.menuItem_Permalink}
                  onchange={handleInputChange("menuItem_Permalink")}
                />
              </div>
              <div className="col-6">
                <FormInput
                  label="Image Alt Text"
                  type="text"
                  placeHolder="Enter Image Alt Text"
                  name="menuItem_ImageAltText"
                  value={formState.menuItem_ImageAltText}
                  onchange={handleInputChange("menuItem_ImageAltText")}
                />
              </div>
              <div className="col-12">
                <label className="form-label fw-medium">SEO Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Enter Description of SEO"
                  value={formState.menuItem_SEODescription}
                  onChange={handleInputChange("menuItem_SEODescription")}
                  style={{
                    border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                    background: isDarkMode ? "#2d3436" : "#F9F9F9",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderShimmer = () => (
    <div className="bg-white p-4 rounded-3 shadow-sm">
      <div className="shimmer">
        <div className="d-flex align-items-center mb-4">
          <div className="shimmer-line w-25 me-3" />
          <div className="shimmer-button" />
        </div>
        <div className="row mb-4 align-items-center">
          <div className="col-md-3 text-center">
            <div className="shimmer-image" />
          </div>
          <div className="col-md-9">
            <div className="row row-cols-2 g-3">
              <div className="col">
                <div className="shimmer-line w-75" />
              </div>
              <div className="col">
                <div className="shimmer-line w-50" />
              </div>
              <div className="col">
                <div className="shimmer-line w-60" />
              </div>
              <div className="col">
                <div className="shimmer-line w-40" />
              </div>
              <div className="col">
                <div className="shimmer-line w-55" />
              </div>
            </div>
          </div>
        </div>
        <div className="row g-4">
          <div className="col-md-6">
            <div className="shimmer-line w-90" />
            <div className="shimmer-line w-80" />
            <div className="shimmer-block h-120" />
          </div>
          <div className="col-md-6">
            <div className="shimmer-line w-85" />
            <div className="shimmer-line w-70" />
            <div className="shimmer-block h-120" />
          </div>
        </div>
        <div className="shimmer-block h-140 mt-4" />
      </div>
    </div>
  );

  useUnsavedChangesGuard({
    isDirty: !loading && isDirty,
    onSave: () => handleUpdate({ exitAfterSave: false }),
    onDiscard: () => setInitialLoadedDataSnapshot(getFormSnapshot()),
  });

  return (
    <div>
      
      {renderBreadcrumb()}
      <GridSectionLoader loading={loading} minHeight="280px">
<div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>General</PanelHeader>
          <PanelBody>
            {initialLoading ? (
              renderShimmer()
            ) : (
              <div 
                className="p-4 rounded-3 shadow-sm"
                style={{
                  backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : '#000000',
                }}
              >
              
                <div className="row mb-4 align-items-center">
                  <div className="col-md-3 text-center">
                    <ImageUpload
                      imageUrl={imageState.menuItem_Image}
                      imageFile={imageState.imageFile}
                      setImage={(url, file, opts) => {
                        setImageState({ menuItem_Image: url, imageFile: file });
                        if (opts?.altText != null && String(opts.altText).trim() !== "") {
                          setFormState((prev) => ({
                            ...prev,
                            menuItem_ImageAltText: String(opts.altText).trim().slice(0, 500),
                          }));
                        }
                      }}
                      cropModalTitle="Crop menu item image"
                      mediaLibraryTitle="Menu image library"
                    />
                  </div>
                  <div className="col-md-9">{renderToggles()}</div>
                </div>
                {renderFormFields()}
                {!toggles.menuItem_HasVariant && !isVariantLineItem && renderAdditionalOptions()}
                <div className="col-12 mt-4">
                  <div 
                    className="card p-3"
                    style={{
                      backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                      border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
                      color: isDarkMode ? '#ffffff' : '#000000',
                    }}
                  >
                    <h5 
                      className="card-title mb-3"
                      style={{
                        color: isDarkMode ? '#ffffff' : '#000000',
                      }}
                    >
                      Selected Categories *
                    </h5>
                    <CategorySelector
                      selectedCategories={selectors.selectedParentCategory}
                      setSelectedCategories={(updater) =>
                        setSelectors((prev) => {
                          const currentCats = prev.selectedParentCategory;
                          const newCats =
                            typeof updater === "function"
                              ? updater(currentCats)
                              : updater;
                          return { ...prev, selectedParentCategory: newCats };
                        })
                      }
                      showModal={modals.showParentCategoryModal}
                      setShowModal={(val) =>
                        setModals((prev) => ({
                          ...prev,
                          showParentCategoryModal: val,
                        }))
                      }
                      onModalSubmit={handleSelectorSubmit(
                        "selectedParentCategory"
                      )}
                      setLoading={setLoading}
                    />
                    {selectors.selectedParentCategory.length > 1 && (
                      <div className="mt-4">
                        <div className="d-flex justify-content-center">
                          <div
                            className="reporting-category-disclaimer"
                            style={{
                              background: isDarkMode ? '#404040' : '#f8f9fa',
                              borderRadius: '8px',
                              padding: '12px 16px',
                              border: isDarkMode ? '1px solid #555' : '1px solid #e9ecef',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              fontSize: '14px',
                              color: isDarkMode ? '#ffffff' : '#6c757d',
                              maxWidth: 'fit-content',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = isDarkMode ? '#555' : '#e9ecef';
                              e.currentTarget.style.borderColor = themeColor;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = isDarkMode ? '#404040' : '#f8f9fa';
                              e.currentTarget.style.borderColor = isDarkMode ? '#555' : '#e9ecef';
                            }}
                          >
                            <i className="fa fa-info-circle" style={{ fontSize: '14px', color: themeColor }}></i>
                            <span style={{ color: isDarkMode ? '#ffffff' : '#6c757d' }}>
                              Reporting Category: <strong style={{ color: isDarkMode ? '#ffffff' : '#495057' }}>
                                {reportingCategory?.rootCat_Name || reportingCategory?.rC_Name || "Not selected"}
                              </strong>
                            </span>
                            <button
                              onClick={() =>
                                setModals((prev) => ({
                                  ...prev,
                                  showReportingCategoryModal: true,
                                }))
                              }
                              style={{
                                background: 'transparent',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '4px',
                                color: themeColor,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '24px',
                                height: '24px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = themeColor;
                                e.currentTarget.style.color = 'white';
                                e.currentTarget.style.transform = 'scale(1.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = themeColor;
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                            >
                              <i className="fa fa-edit" style={{ fontSize: '10px' }}></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {formState.menuItem_Type !== "Combo" && (
                  <div className="col-12 mt-4">
                    <ModifierGroupsSelector
                      isEnabled={toggles.modifierGroupsEnabled}
                      setIsEnabled={handleToggle("modifierGroupsEnabled")}
                      selectedGroups={selectors.selectedModifierGroups}
                      setSelectedGroups={(groups) =>
                        setSelectors((prev) => ({
                          ...prev,
                          selectedModifierGroups: dedupeModifierGroups(Array.isArray(groups) ? groups : []),
                        }))
                      }
                      showModal={modals.showModifierModal}
                      setShowModal={(val) =>
                        setModals((prev) => ({
                          ...prev,
                          showModifierModal: val,
                        }))
                      }
                      onModalSubmit={handleSelectorSubmit(
                        "selectedModifierGroups"
                      )}
                      setLoading={setLoading}
                    />
                  </div>
                )}
                <div className="col-12 mt-4">
                  <SupplierSelector
                    isEnabled={toggles.suppliersEnabled}
                    setIsEnabled={handleToggle("suppliersEnabled")}
                    selectedSuppliers={selectors.selectedSuppliers}
                    setSelectedSuppliers={(suppliers) =>
                      setSelectors((prev) => ({
                        ...prev,
                        selectedSuppliers: Array.isArray(suppliers) ? suppliers : [],
                      }))
                    }
                    showModal={modals.showSupplierModal}
                    setShowModal={(val) =>
                      setModals((prev) => ({ ...prev, showSupplierModal: val }))
                    }
                    onModalSubmit={handleSelectorSubmit("selectedSuppliers")}
                    setLoading={setLoading}
                  />
                </div>
                <div className="col-12 mt-4">
                  <AllergenSelector
                    isEnabled={toggles.allergensEnabled}
                    setIsEnabled={handleToggle("allergensEnabled")}
                    selectedAllergens={selectors.selectedAllergens}
                    setSelectedAllergens={(allergens) =>
                      setSelectors((prev) => ({
                        ...prev,
                        selectedAllergens: resolveSelectedAllergens(
                          allergens,
                          allAllergens
                        ),
                      }))
                    }
                    showModal={modals.showAllergenModal}
                    setShowModal={(val) =>
                      setModals((prev) => ({ ...prev, showAllergenModal: val }))
                    }
                    onModalSubmit={handleSelectorSubmit("selectedAllergens")}
                    setLoading={setLoading}
                    allergens={allAllergens} 
                  />
                </div>
                {formState.menuItem_Type === "Combo" && (
                  <div className="col-12 mt-4">
                    <h5 className="card-title mb-3">Selected Products</h5>
                    <div 
                      className="card p-4"
                      style={{
                        backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                        border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
                        color: isDarkMode ? '#ffffff' : '#000000',
                      }}
                    >
                      <div className="mb-3">
                        <ToggleSwitch
                          isChecked={toggles.productsEnabled}
                          onToggle={handleToggle("productsEnabled")}
                          label="Select Products"
                          name="productsEnabled"
                          themeColor={themeColor}
                        />
                      </div>
                      {toggles.productsEnabled && (
                        <>
                          {selectors.selectedProducts.length > 0 ? (
                            <div className="mt-3">
                              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                                {selectors.selectedProducts?.map((product) => (
                                  <div
                                    key={product.menuItem_ID}
                                    className="col"
                                  >
                                    <div
                                      className="product-chip"
                                      style={{
                                        background: '#ffffff',
                                        borderRadius: '12px',
                                        padding: '16px 20px',
                                        border: '2px solid #e9ecef',
                                        transition: 'all 0.2s ease',
                                        cursor: 'default',
                                        position: 'relative',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = '#007bff';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.15)';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = '#e9ecef';
                                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                      }}
                                    >
                                      <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                          {/* Product Image/Icon */}
                                          <div
                                            style={{
                                              width: '36px',
                                              height: '36px',
                                              background: '#f8f9fa',
                                              borderRadius: '8px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              marginRight: '12px',
                                              border: '1px solid #e9ecef',
                                              overflow: 'hidden'
                                            }}
                                          >
                                            {product.menuItem_Image ? (
                                              <img
                                                src={product.menuItem_Image}
                                                alt={product.menuItem_Name}
                                                style={{
                                                  width: '100%',
                                                  height: '100%',
                                                  objectFit: 'cover',
                                                  borderRadius: '6px'
                                                }}
                                              />
                                            ) : (
                                              <i className="fa fa-box" style={{ fontSize: '14px', color: '#6c757d' }}></i>
                                            )}
                                          </div>

                                          {/* Product Name */}
                                          <div>
                                            <span
                                              style={{
                                                fontSize: '15px',
                                                fontWeight: '500',
                                                color: '#495057'
                                              }}
                                            >
                                              {product.menuItem_Name}
                                            </span>
                                          </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                          {/* Quantity Input */}
                                          <FormInput
                                            type="text"
                                            value={product.availableQuantity}
                                            onchange={(e) =>
                                              handleProductQuantityChange(
                                                product.menuItem_ID,
                                                e.target.value
                                              )
                                            }
                                            placeHolder="Qty"
                                            style={{
                                              width: "60px",
                                              fontSize: '12px',
                                              padding: '4px 8px',
                                              border: '1px solid #e9ecef',
                                              borderRadius: '4px',
                                              textAlign: 'center'
                                            }}
                                          />

                                          {/* Remove Button */}
                                          <button
                                            onClick={() =>
                                              removeProduct(product.menuItem_ID)
                                            }
                                            style={{
                                              background: 'transparent',
                                              border: 'none',
                                              borderRadius: '6px',
                                              width: '28px',
                                              height: '28px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              cursor: 'pointer',
                                              transition: 'all 0.2s ease',
                                              color: '#dc3545'
                                            }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.background = '#f8d7da';
                                              e.currentTarget.style.color = '#721c24';
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.background = 'transparent';
                                              e.currentTarget.style.color = '#dc3545';
                                            }}
                                          >
                                            <i
                                              className="fa fa-times"
                                              style={{
                                                fontSize: '12px'
                                              }}
                                            ></i>
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div className="d-flex justify-content-center mt-4">
                                <button
                                  onClick={() =>
                                    setModals((prev) => ({
                                      ...prev,
                                      showProductModal: true,
                                    }))
                                  }
                                  style={{
                                    background: '#007bff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '10px 20px',
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#0056b3';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#007bff';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }}
                                >
                                  <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                                  Add More Products
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              <div
                                style={{
                                  width: '60px',
                                  height: '60px',
                                  background: '#f8f9fa',
                                  borderRadius: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  margin: '0 auto 16px',
                                  border: '1px solid #e9ecef'
                                }}
                              >
                                <i className="fa fa-box" style={{ fontSize: '24px', color: '#6c757d' }}></i>
                              </div>
                              <p style={{ color: '#6c757d', fontSize: '15px', marginBottom: '20px' }}>No products selected</p>
                              <button
                                onClick={() =>
                                  setModals((prev) => ({
                                    ...prev,
                                    showProductModal: true,
                                  }))
                                }
                                style={{
                                  background: '#007bff',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '10px 20px',
                                  color: 'white',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  margin: '0 auto'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#0056b3';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#007bff';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                                Add Products
                              </button>
                            </div>
                          )}
                          <ProductMultiSelectionModal
                            isEnabled={toggles.productsEnabled}
                            setIsEnabled={handleToggle("productsEnabled")}
                            selectedProducts={selectors.selectedProducts}
                            setSelectedProducts={(products) =>
                              setSelectors((prev) => ({
                                ...prev,
                                selectedProducts: products?.map((p) => ({
                                  ...p,
                                  availableQuantity: p.availableQuantity || "1",
                                })),
                              }))
                            }
                            showModal={modals.showProductModal}
                            setShowModal={(val) =>
                              setModals((prev) => ({
                                ...prev,
                                showProductModal: val,
                              }))
                            }
                            onModalSubmit={handleSelectorSubmit(
                              "selectedProducts"
                            )}
                            setLoading={setLoading}
                            products={products}
                            showButton={true}
                          />
                        </>
                      )}
                    </div>
                  </div>
                )}

                <div className="col-12 mt-4">
                  <MenuGroupsSelector
                    isEnabled={toggles.menuGroupsEnabled}
                    setIsEnabled={handleToggle("menuGroupsEnabled")}
                    selectedGroups={selectors.selectedMenuGroups}
                    setSelectedGroups={(groups) =>
                      setSelectors((prev) => ({
                        ...prev,
                        selectedMenuGroups: Array.isArray(groups) ? groups : [],
                      }))
                    }
                    showModal={modals.showMenuGroupModal}
                    setShowModal={(val) =>
                      setModals((prev) => ({
                        ...prev,
                        showMenuGroupModal: val,
                      }))
                    }
                    onModalSubmit={handleSelectorSubmit("selectedMenuGroups")}
                    setLoading={setLoading}
                  />
                </div>

                {renderSEO()}

                <div className="row mt-4" style={{ display: 'none' }}>
                  <div className="col-6">
                    <div 
                      className="card p-3 mb-4"
                      style={{
                        backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                        border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
                        color: isDarkMode ? '#ffffff' : '#000000',
                      }}
                    >
                      <h5 className="card-title mb-3">Other</h5>
                      <div className="row g-3">
                        <div className="col-6 mb-3">
                          <label 
                            className="form-label fw-medium"
                            style={{ color: isDarkMode ? '#ffffff' : '#000000' }}
                          >
                            Available Start Time
                          </label>
                          <DateTime
                            dateFormat={false}
                            timeFormat="HH:mm"
                            value={formState.menuItem_ASTime}
                            onChange={handleASTimeChange}
                            inputProps={{
                              placeholder: "Select start time (e.g., 09:00)",
                              className: `form-control ${isDarkMode ? 'dark-mode-input' : 'light-mode-input'}`,
                              style: {
                                borderRadius: "10px",
                                border: isDarkMode ? '1px solid #404040' : '1px solid #D9D9D9',
                                background: isDarkMode ? '#2d3436' : '#F9F9F9',
                                color: isDarkMode ? '#ffffff' : '#000000',
                                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                              }
                            }}
                          />
                        </div>
                        <div className="col-6 mb-3">
                          <label 
                            className="form-label fw-medium"
                            style={{ color: isDarkMode ? '#ffffff' : '#000000' }}
                          >
                            Available End Time
                          </label>
                          <DateTime
                            dateFormat={false}
                            timeFormat="HH:mm"
                            value={formState.menuItem_AETime}
                            onChange={handleAETimeChange}
                            inputProps={{
                              placeholder: "Select end time (e.g., 17:00)",
                              className: `form-control ${isDarkMode ? 'dark-mode-input' : 'light-mode-input'}`,
                              style: {
                                borderRadius: "10px",
                                border: isDarkMode ? '1px solid #404040' : '1px solid #D9D9D9',
                                background: isDarkMode ? '#2d3436' : '#F9F9F9',
                                color: isDarkMode ? '#ffffff' : '#000000',
                                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-6 mb-3">
                          <FormInput
                            label="Rating"
                            value={formState.menuItem_Rating}
                            onchange={handleInputChange("menuItem_Rating")}
                            type="number"
                            placeHolder="Rating (0-5)"
                            name="menuItem_Rating"
                          />
                        </div>
                        <div className="col-6 mb-3">
                          <FormInput
                            label="Barcode"
                            value={formState.menuItem_BarCode}
                            onchange={handleInputChange("menuItem_BarCode")}
                            type="text"
                            placeHolder="Barcode"
                            name="menuItem_BarCode"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-6">{renderAdditionalToggles()}</div>
                </div>

                {renderVariantDetailModal()}

                <CustomModal
                  title="What type of item do you want to update"
                  fontSize="18px"
                  fontWeight={400}
                  onClose={() =>
                    setModals((prev) => ({ ...prev, isModalOpen: false }))
                  }
                  isOpen={modals.isModalOpen}
                  isCentered={true}
                >
                  <ChoiceButtonGrid
                    activeButton={formState.menuItem_RootType}
                    handleClick={handleProductTypeSelect}
                  />
                </CustomModal>

                <ReportingCategoryModel
                  isModalOpen={modals.showReportingCategoryModal}
                  setIsModalOpen={(val) =>
                    setModals((prev) => ({
                      ...prev,
                      showReportingCategoryModal: val,
                    }))
                  }
                  onCategorySubmit={handleReportingCategorySubmit}
                  compID={CompID}
                  selectedCategory={reportingCategory}
                  categories={selectors.selectedParentCategory}
                  showButton={true}
                  onRefresh={refreshReportingCategoriesFromTree}
                />
              </div>
            )}
          </PanelBody>
        </Panel>
      </div>
</GridSectionLoader>
    </div>
  );
};

export default UpdateItem;
